import { useState, useMemo, useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useDepartments } from '@/hooks/use-departments';
import { useCategories } from '@/hooks/use-categories';
import { useModifierGroups } from '@/hooks/use-modifiers';
import { useLocations } from '@/hooks/use-locations';
import { useSpPayees, useCreateSpPayee } from '@/hooks/use-sp-payees';
import { useLocationStore } from '@/stores/location-store';
import { useAuthStore } from '@/stores/auth-store';
import { accessibleLocations, seesAllLocations } from '@/lib/location-access';
import { PrivateWarehouseSection } from '@/components/products/private-warehouse-section';
import { useCanSeeCost } from '@/hooks/use-pos-config';
import { usePermissions } from '@/hooks/use-permissions';
import { useProductPriceHistory, useProductAuditLog, useVendorPriceHistory } from '@/hooks/use-products';
import { useUsers } from '@/hooks/use-users';
import { formatCurrency, formatDateTime, cn } from '@/lib/utils';
import { unitOptionsFor, allowedSellingUnitsFor, UNIT_OPTIONS, formatCrvOption } from '@/lib/constants';
import { useCrvOptions } from '@/hooks/use-crv-options';
import { useBarcodeSettings, useGenerateBarcode } from '@/hooks/use-barcode-settings';
import { CrvOptionsEditor } from './crv-options-editor';
import { CostCalculatorModal } from './cost-calculator-modal';
import { grossMarginPct } from '@/lib/pricing';
import { toast } from '@/stores/toast-store';
import { validatePhone } from '@/lib/validators';
import { MapPin, Plus, Trash2, History, ClipboardList, X, Calculator, ChevronRight, Info, Store } from 'lucide-react';
import { DateInput } from '@/components/ui/date-input';
import { PhoneInput } from '@/components/ui/phone-input';
import { MultiSelect } from '@/components/ui/multi-select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { ScopeChip, ScopeDot } from '@/components/products/field-scope';
import { scopeCardCls, distinctValues, type FieldScope } from '@/lib/product-field-scope';
import { isWarehouseType } from '@/lib/location-type';
import { canSeeProductIntelligence } from '@/lib/product-intelligence';
import { IntelligenceTab } from '@/components/products/intelligence/intelligence-tab';

/** Roles that may see the gross margin under Selling Price. */
const MARGIN_VISIBLE_ROLES: readonly string[] = ['owner', 'admin', 'manager'];

interface LocationInventory {
  locationId: string;
  locationName: string;
  assigned: boolean;
  inStockQty: string;
  reorderThreshold: string;
  reorderQuantity: string;
  sellingPrice: string;
  cost: string;
  // Per-location overrides for the product's stocking + purchasing units.
  // Empty string = "use product default" (falls back to products.default_*_unit).
  // Lets a warehouse stock in 'pallet' while the same product sells in 'each'
  // at retail.
  defaultSellingUnit: string;
  defaultPurchasingUnit: string;
}

interface UnitEntry {
  unitName: string;
  baseQty: string;
  sellingPrice: string;
  cost: string;
  upc: string;
}

interface VendorEntry {
  vendorId: string;
  vendorItemCode: string;
  vendorProductName: string;
  unitName: string;
  vendorPrice: string;
  priceReason: string;
  effectiveDate: string;
}

/**
 * Original indices of `list`, cheapest first; a missing or unparseable price
 * sorts last. Returns indices rather than entries because every vendor mutation
 * (`updateVendor`, `removeVendor`) addresses a row by its index in state — the
 * displayed order must never become the identity of a row.
 */
function sortVendorIdx(list: VendorEntry[]): number[] {
  const priceOf = (entry: VendorEntry) => {
    const n = entry.vendorPrice ? parseFloat(entry.vendorPrice) : NaN;
    return Number.isFinite(n) ? n : Infinity;
  };
  return list.map((_, i) => i).sort((a, b) => priceOf(list[a]) - priceOf(list[b]));
}

/**
 * Keeps a money field typeable in a CONTROLLED input.
 *
 * `<input type="number">` reports an in-progress value like "12." as the empty
 * string, so a controlled component writes '' back to state and the digits
 * before the point are gone for good — typing "12.34" leaves "34". Verified in
 * this app, and it is why this cell is a text input with inputMode="decimal".
 *
 * Accepts digits and one decimal point, at most two places. State stays a
 * string, exactly as before, so the parseFloat at submit is unchanged.
 */
function sanitizeMoney(raw: string): string {
  let s = raw.replace(/[^\d.]/g, '');
  const first = s.indexOf('.');
  if (first !== -1) s = s.slice(0, first + 1) + s.slice(first + 1).replace(/\./g, '');
  const [whole, dec] = s.split('.');
  return dec === undefined ? whole : `${whole}.${dec.slice(0, 2)}`;
}

/** A stored price ("12.00", null, missing) as a number, or null when absent. */
function toNumberOrNull(value: string | null | undefined): number | null {
  if (value == null || value === '') return null;
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : null;
}

/** One column template, shared by the vendor table's head and its rows so the
 *  two can never drift apart. Explicit widths rather than scale values: a grid
 *  track list is not something the spacing scale can express. */
const VENDOR_GRID =
  'grid grid-cols-[28px_minmax(180px,1.3fr)_104px_116px_132px_minmax(160px,1.2fr)_128px_56px] items-center gap-2';

/** slate-600, not muted-foreground: these heads are 11px, which needs 4.5:1, and
 *  muted-foreground drops under that over the row hover tint. */
const VENDOR_HEAD = 'text-[11px] font-semibold uppercase tracking-wide text-slate-600';

/** Field label inside an inventory location card. Same 11px uppercase treatment
 *  as VENDOR_HEAD, and slate-600 for the same contrast reason. Every field in a
 *  card carries one, so no value depends on a column header any more. */
const INV_FIELD_LABEL = 'mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-600';

interface ProductFormProps {
  initialData?: any;
  onSubmit: (data: any) => void;
  isSubmitting: boolean;
}

// Unit dropdowns use unitOptionsFor() from @/lib/constants so each select
// keeps any legacy value (e.g. an older product saved with "kg") visible while
// still offering only the canonical UNIT_OPTIONS for new picks.

export function ProductForm({ initialData, onSubmit, isSubmitting }: ProductFormProps) {
  const [activeTab, setActiveTab] = useState('product-pricing');
  const canSeeCostPrice = useCanSeeCost();
  const { data: deptData } = useDepartments();
  const { data: catData } = useCategories();
  const { data: modifierGroupData } = useModifierGroups();
  const { data: locData, isLoading: locationsLoading } = useLocations();
  const { data: vendorData } = useSpPayees({ status: 'active' });
  const [showAddVendorModal, setShowAddVendorModal] = useState(false);
  const [showCostCalculatorModal, setShowCostCalculatorModal] = useState(false);
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const authUser = useAuthStore((s) => s.user);
  // Whoever may add a product sets its first barcode, price and vendors. On a
  // saved product each needs its own permission, and the products service
  // refuses a real change whatever this form shows.
  const permissions = usePermissions();
  const isSavedProduct = !!initialData?.id;
  const canSaveProduct = permissions.has(isSavedProduct ? 'products.edit' : 'products.add');
  const lockBarcodes = isSavedProduct && !permissions.has('products.barcodes');
  const lockPrices = isSavedProduct && !permissions.has('products.prices');
  const lockVendors = isSavedProduct && !permissions.has('products.vendors');
  const canAddVendor = permissions.has('vendors.add');
  const lockedParts = [
    ...(lockBarcodes ? ['barcodes'] : []),
    ...(lockPrices ? ['prices'] : []),
    ...(lockVendors ? ['vendors'] : []),
  ];
  // The margin under Selling Price is for the people who set pricing — owners,
  // admins and managers. Everyone else just sees the price.
  const canSeeMargin = MARGIN_VISIBLE_ROLES.includes(authUser?.role ?? '');
  const canSeeIntelligence = canSeeProductIntelligence(authUser?.role);
  const tenant = useAuthStore((s) => s.tenant);
  // Tenant-configurable CRV fee options (falls back to defaults while loading).
  const { options: crvOptions } = useCrvOptions();
  const [crvEditorOpen, setCrvEditorOpen] = useState(false);
  // The CRV options are a tenant-wide setting that stays with Owners and Admins.
  const canManageCrvOptions = permissions.atLeast('admin');
  // Spotlight mode — fades the tenant-level cards so the fields that follow
  // the header's location picker are the only ones left lit. Off by default;
  // it answers a question, it isn't a way to read the page.
  const [scopeSpotlight, setScopeSpotlight] = useState(false);

  // Location scoping. Owner / super_admin (and the tenant-wide admin with no
  // explicit assignment) reach every location; admin / manager / employee are
  // otherwise limited to the locations granted via user.locationIds — even an
  // admin shouldn't be able to mutate a warehouse or sister-store row when
  // they were granted permission to a single store.
  //
  // Rows for locations outside those grants used to render read-only, which
  // meant a store manager could still read every other store's price and
  // stock. They are now filtered out entirely (see initialLocationInventory),
  // matching the header's location picker and the products service, which no
  // longer returns those rows at all.
  const canEditAllLocations = seesAllLocations(authUser);
  const allowedLocationIds = useMemo(() => {
    if (canEditAllLocations) return null;
    return new Set(authUser?.locationIds ?? []);
  }, [canEditAllLocations, authUser]);
  const canEditLocation = (locationId: string) => canEditAllLocations || (allowedLocationIds?.has(locationId) ?? false);

  // Warehouse pricing/qty inputs are gated to managerial roles only.
  // Employees never edit warehouse rows even if they have location access.
  const canEditWarehouseInventory = ['super_admin', 'owner', 'admin', 'manager', 'inventory_manager'].includes(
    authUser?.role || '',
  );

  const departments = deptData?.data ?? [];
  const allCategories = catData?.data ?? [];
  const allModifierGroups = modifierGroupData?.data ?? [];
  const allLocations = locData?.data ?? [];
  const allVendors = vendorData?.data ?? [];
  // The list holds this store's active vendors, so one assigned earlier can be
  // missing from it. The product carries that vendor's name, so the row keeps it.
  const vendorNames = useMemo(() => {
    const names = new Map<string, string>(allVendors.map((p: any) => [p.id, p.name]));
    for (const p of initialData?.payees ?? []) {
      if (p.payeeName && !names.has(p.payeeId)) names.set(p.payeeId, p.payeeName);
    }
    return names;
  }, [allVendors, initialData?.payees]);
  const locationTypeMap = useMemo(
    () => new Map<string, string>(allLocations.map((l: any) => [l.id, l.type])),
    [allLocations],
  );
  const isWarehouseLocation = (locationId: string) => isWarehouseType(locationTypeMap.get(locationId));
  // Form context follows the header location selector. In a warehouse context
  // we hide the product-level "individual" retail price + cost calculator
  // (those are store/retail concepts). The Location Inventory table still
  // shows both kinds so users can see prices/thresholds everywhere, but the
  // off-context kind is locked to read-only so a store-edit can't bleed into
  // warehouse pricing and vice versa.
  const formContext: 'store' | 'warehouse' | 'all' = !selectedLocationId
    ? 'all'
    : isWarehouseLocation(selectedLocationId)
      ? 'warehouse'
      : 'store';
  const isWarehouseContext = formContext === 'warehouse';
  // Store locations whose mapped private warehouses to show: the selected
  // store, or (no selection) every store this user can access. None when a
  // warehouse is selected — private warehouses supply stores only.
  const privateWarehouseLocationIds = useMemo(() => {
    if (formContext === 'warehouse') return [];
    if (selectedLocationId) return [selectedLocationId];
    return accessibleLocations(authUser, allLocations)
      .filter((l: any) => !isWarehouseType(l.type))
      .map((l: any) => l.id as string);
  }, [formContext, selectedLocationId, authUser, allLocations]);
  // True when the row's type does NOT match the active form context. Used to
  // disable editing on off-context rows so they render as read-only.
  const isOffContextRow = (locationId: string) => {
    if (formContext === 'all') return false;
    const rowIsWarehouse = isWarehouseLocation(locationId);
    return formContext === 'warehouse' ? !rowIsWarehouse : rowIsWarehouse;
  };
  const isLocationPricingEditable = (locationId: string) => {
    if (!canEditLocation(locationId)) return false;
    if (isWarehouseLocation(locationId) && !canEditWarehouseInventory) return false;
    if (isOffContextRow(locationId)) return false;
    return true;
  };

  // Build location inventory — filter to selected location when one is chosen
  const initialLocationInventory = useMemo((): LocationInventory[] => {
    if (!allLocations.length) return [];

    // Every location the user is allowed to reach — checkboxes control
    // assignment. Locations outside their grants are dropped here rather than
    // rendered read-only, so a store manager can't read a sister store's price
    // or stock off this table. Order follows the API (stores by createdAt,
    // then warehouses by createdAt).
    //
    // Dropping them is safe for saves: the products service scopes its
    // location/price replace to the caller's own locations, so the rows this
    // form never sends survive untouched.
    const visibleLocations = accessibleLocations(authUser, allLocations);

    const existingMap = new Map<string, any>();
    if (initialData?.locations) {
      for (const loc of initialData.locations) {
        existingMap.set(loc.locationId, loc);
      }
    }

    const priceMap = new Map<string, { sellingPrice: string; cost: string }>();
    if (initialData?.prices) {
      for (const p of initialData.prices) {
        priceMap.set(p.locationId, { sellingPrice: p.sellingPrice, cost: p.cost || '' });
      }
    }

    return visibleLocations.map((loc: any) => {
      const existing = existingMap.get(loc.id);
      const priceData = priceMap.get(loc.id);
      if (existing) {
        return {
          locationId: loc.id,
          locationName: loc.name,
          assigned: true,
          inStockQty: existing.inStockQty?.toString() || '0',
          reorderThreshold: existing.reorderThreshold?.toString() || '',
          reorderQuantity: existing.reorderQuantity?.toString() || '',
          sellingPrice: priceData?.sellingPrice || '',
          cost: priceData?.cost || '',
          defaultSellingUnit: existing.defaultSellingUnit || '',
          defaultPurchasingUnit: existing.defaultPurchasingUnit || '',
        };
      }
      return {
        locationId: loc.id,
        locationName: loc.name,
        assigned: !initialData && selectedLocationId === loc.id,
        inStockQty: '0',
        reorderThreshold: '',
        reorderQuantity: '',
        sellingPrice: '',
        cost: '',
        defaultSellingUnit: '',
        defaultPurchasingUnit: '',
      };
    });
  }, [allLocations, initialData, selectedLocationId, authUser]);

  const locationPrice = selectedLocationId
    ? initialData?.prices?.find((p: any) => p.locationId === selectedLocationId)
    : null;

  const [form, setForm] = useState({
    name: initialData?.name || '',
    displayName: initialData?.displayName || '',
    primaryBarcode: initialData?.primaryBarcode || '',
    // 'manual' | 'auto' — drives the Auto badge; sent back so the server keeps
    // the provenance when the code is untouched.
    barcodeSource: (initialData?.barcodeSource as string) || 'manual',
    additionalBarcodes: ((initialData?.additionalBarcodes as any[]) || []).map((b: any) =>
      typeof b === 'object' && b !== null ? (b.barcode ?? String(b)) : String(b),
    ),
    sku: initialData?.sku || '',
    customField1: initialData?.customField1 || '',
    customField2: initialData?.customField2 || '',
    customField3: initialData?.customField3 || '',
    building: initialData?.building || '',
    aisle: initialData?.aisle || '',
    bin: initialData?.bin || '',
    departmentId: initialData?.departmentId || '',
    sortOrder:
      initialData?.sortOrder === null || initialData?.sortOrder === undefined ? '' : String(initialData.sortOrder),
    sellingPrice: selectedLocationId
      ? locationPrice?.sellingPrice || ''
      : initialData?.sellingPrice || initialData?.cashPrice || '',
    cost: selectedLocationId ? locationPrice?.cost || '' : initialData?.cost || '',
    soldByWeight: initialData?.soldByWeight || false,
    ageVerification: initialData?.ageVerification || false,
    ebtEligible: initialData?.ebtEligible ?? (initialData?.excludeEbt != null ? !initialData.excludeEbt : true),
    couponEligible: initialData?.couponEligible ?? true,
    excludeKitchenPrint: initialData?.excludeKitchenPrint || false,
    taxable: initialData?.taxable ?? (initialData?.taxExempt != null ? !initialData.taxExempt : true),
    promptQuantity: initialData?.promptQuantity || false,
    isVariablePrice: initialData?.isVariablePrice || false,
    crvFee: initialData?.crvFee || 0,
    // Expiry/batch pairs are per store: GET /products/:id?locationId returns
    // the active location's values (expiryDate = expiryDate1).
    expiryDate: initialData?.expiryDate ? String(initialData.expiryDate).slice(0, 10) : '',
    batchNo1: initialData?.batchNo1 || '',
    expiryDate2: initialData?.expiryDate2 ? String(initialData.expiryDate2).slice(0, 10) : '',
    batchNo2: initialData?.batchNo2 || '',
    defaultSellingUnit: initialData?.defaultSellingUnit || 'each',
    defaultPurchasingUnit: initialData?.defaultPurchasingUnit || 'each',
    categoryIds:
      initialData?.categories?.map((c: any) => c.categoryId) ||
      initialData?.tags?.map((t: any) => t.categoryId || t.tagId) ||
      [],
    modifierGroupIds:
      initialData?.modifierGroupIds || initialData?.modifierGroups?.map((m: any) => m.modifierGroupId) || [],
  });

  const [departmentError, setDepartmentError] = useState('');
  const [barcodeError, setBarcodeError] = useState('');
  // Generation is gated twice: our team enables the tenant, then the tenant
  // saves a starting barcode. `active` is both.
  const barcodeSettings = useBarcodeSettings(tenant?.barcodeAutogenEnabled === true);
  const barcodeAutogenActive = barcodeSettings.data?.data?.active === true;
  const generateBarcode = useGenerateBarcode();
  // Duplicate errors that land on the Additional Barcodes list rather than the
  // primary field — previously the only dedup was against this form's own chips.
  const [additionalBarcodeError, setAdditionalBarcodeError] = useState('');
  // The product that already owns a conflicting code, so we can link straight
  // to it instead of making the user go hunting.
  const [barcodeConflict, setBarcodeConflict] = useState<{ id: string; name: string } | null>(null);
  const [skuError, setSkuError] = useState('');
  const [unitsError, setUnitsError] = useState('');
  const [sellingPriceError, setSellingPriceError] = useState('');
  const [locationError, setLocationError] = useState('');
  const uomSectionRef = useRef<HTMLDivElement>(null);

  // Cost Calculator — seeded from the first case/pack unit when the product has
  // one, so reopening it on an existing product starts from what's already
  // there. The calculator owns its own working state from that point on.
  const costCalcInitial = useMemo(() => {
    const caseUnit = initialData?.units?.find(
      (u: any) => u.unitName?.toLowerCase() === 'case' || u.unitName?.toLowerCase() === 'pack',
    );
    return {
      unitType: caseUnit?.unitName || 'case',
      costPerCase: caseUnit?.cost?.toString() || '',
      unitsInCase: caseUnit?.baseQty?.toString() || '',
    };
  }, [initialData]);

  // Additional barcodes
  const [newBarcode, setNewBarcode] = useState('');

  // Case/Pack units
  const [units, setUnits] = useState<UnitEntry[]>(
    initialData?.units?.map((u: any) => ({
      unitName: u.unitName || '',
      baseQty: u.baseQty?.toString() || '',
      sellingPrice: u.sellingPrice?.toString() || '',
      cost: u.cost?.toString() || '',
      upc: u.upc || '',
    })) || [],
  );

  // Vendor management
  const [vendors, setVendors] = useState<VendorEntry[]>(
    initialData?.payees?.map((v: any) => ({
      vendorId: v.payeeId || '',
      vendorItemCode: v.payeeItemCode || '',
      vendorProductName: v.vendorProductName || '',
      unitName: v.unitName || 'each',
      vendorPrice: v.vendorPrice?.toString() || '',
      priceReason: '',
      effectiveDate: new Date().toISOString().slice(0, 10),
    })) || [],
  );

  // Location overrides
  const [locationOverrides, setLocationOverrides] = useState<Record<string, Partial<LocationInventory>>>({});
  // Per-row override tracking. A row's Cost/Selling Price visibly mirrors
  // its default source (form retail for stores, the matching case row for
  // warehouses) unless the user has explicitly typed in it; once typed,
  // the field is "overridden" for that location.
  //
  // We start with an EMPTY set so the form retail / case row drives every
  // row by default. Existing productPrices rows that diverged from those
  // sources will be unified back to the form/case value on save unless the
  // user re-overrides per row. This matches the "form is the source of
  // truth" expectation — editing the Basic Info Cost/Selling fields
  // propagates to every assigned store row at render time.
  const [priceOverridden, setPriceOverridden] = useState<Set<string>>(() => new Set());
  const isOverridden = (locationId: string, field: 'cost' | 'sellingPrice') =>
    priceOverridden.has(`${locationId}::${field}`);

  // Find the unit row matching a given unit name. Match is normalized
  // (case-insensitive, singular/plural insensitive — same rule the server
  // uses). Returns null when the name is empty or "each", since "each" is
  // the base unit and never lives in product_units.
  const findUnitRowByName = (unitName: string) => {
    const normUnit = (s: string) => {
      const lower = (s || '').toLowerCase().trim();
      return lower.endsWith('s') ? lower.slice(0, -1) : lower;
    };
    const target = normUnit(unitName);
    if (!target || target === 'each') return null;
    return units.find((u) => u.unitName && normUnit(u.unitName) === target) || null;
  };

  // Two pools, two toggles. Each propagates only inside its own kind so a
  // store-side sync can't bleed into warehouse pricing and vice versa.
  const [applyPricesToAllStores, setApplyPricesToAllStores] = useState(false);
  // Tax, coupon, CRV, placement and expiry/batch save to the header location
  // only, unless this is ticked. Without a location they are product-wide.
  const [applyStoreAttrsToAll, setApplyStoreAttrsToAll] = useState(false);
  const storeAttrsScope: FieldScope = selectedLocationId ? 'location' : 'tenant';
  const [applyPricesToAllWarehouses, setApplyPricesToAllWarehouses] = useState(false);
  const inApplyPricesPool = (locationId: string) =>
    isWarehouseLocation(locationId) ? applyPricesToAllWarehouses : applyPricesToAllStores;
  // Editing an existing product from one header location: true for any other
  // location whose price the user typed into directly. Those rows are saved
  // alongside the header location instead of being dropped.
  const isEditedSisterPrice = (locationId: string) =>
    !!selectedLocationId &&
    !!initialData?.id &&
    locationId !== selectedLocationId &&
    !inApplyPricesPool(locationId) &&
    (isOverridden(locationId, 'sellingPrice') || isOverridden(locationId, 'cost'));

  const locationInventory = useMemo(
    () =>
      initialLocationInventory.map((loc) => ({
        ...loc,
        ...locationOverrides[loc.locationId],
      })),
    [initialLocationInventory, locationOverrides],
  );

  // ── Field scope ──────────────────────────────────────────────────────────
  // Cards whose fields follow the header's location picker. With a location
  // active they write that store's row; with none they write the product-wide
  // default that any store may later override — which is a different promise
  // from the flat tenant fields, so it gets the third chip rather than being
  // flattened into "All locations".
  const activeScope: FieldScope = selectedLocationId ? 'location' : 'override';

  // "3 of 6 stores differ" — turns the abstract scope rule into a fact about
  // THIS product, which is what actually tells a user whether to care. Read
  // off locationInventory, which accessibleLocations() has already filtered to
  // the caller's own locations, so no one learns a sister store's numbers.
  const scopeSpread = useMemo(() => {
    const assigned = locationInventory.filter((l) => l.assigned);
    const differing = (pick: (l: (typeof assigned)[number]) => string | undefined) => {
      const values = distinctValues(assigned.map(pick));
      return { distinct: values.length, total: assigned.length, values };
    };
    const prices = assigned.map((l) => parseFloat(l.sellingPrice)).filter((n) => Number.isFinite(n));
    return {
      total: assigned.length,
      sellingPrice: differing((l) => l.sellingPrice),
      sellingUnit: differing((l) => l.defaultSellingUnit),
      purchasingUnit: differing((l) => l.defaultPurchasingUnit),
      priceRange: prices.length ? { min: Math.min(...prices), max: Math.max(...prices) } : null,
    };
  }, [locationInventory]);

  /** "2 of 6 locations use a different value", or null when they all agree. */
  const spreadHint = (s: { distinct: number; total: number }) =>
    s.total > 1 && s.distinct > 1 ? `${s.distinct} different values across ${s.total} locations` : null;

  /** Wording under the unit selectors — the one place on the page where a
   *  single control writes a per-location row, so it can't rely on the card
   *  chip above it and says so in words of its own. */
  const unitScopeHint = (spread: { distinct: number; total: number }) => {
    if (!selectedLocationId) return 'Product-wide default — each location can override it.';
    const differing = spreadHint(spread);
    if (differing) return `Saved for this location only — ${differing}.`;
    return spread.total > 1
      ? `Saved for this location only — all ${spread.total} currently match.`
      : 'Saved for this location only.';
  };

  // The effective selling/purchasing units consider the active location's
  // override so labels, price binding, and validation stay consistent with
  // the dropdowns. Without a location, falls back to the product-level default.
  const effectiveSellingUnit = useMemo(() => {
    if (selectedLocationId) {
      const loc = locationInventory.find((l) => l.locationId === selectedLocationId);
      if (loc?.defaultSellingUnit) return loc.defaultSellingUnit;
    }
    return form.defaultSellingUnit || 'each';
  }, [selectedLocationId, locationInventory, form.defaultSellingUnit]);

  const effectivePurchasingUnit = useMemo(() => {
    if (selectedLocationId) {
      const loc = locationInventory.find((l) => l.locationId === selectedLocationId);
      if (loc?.defaultPurchasingUnit) return loc.defaultPurchasingUnit;
    }
    return form.defaultPurchasingUnit || 'each';
  }, [selectedLocationId, locationInventory, form.defaultPurchasingUnit]);

  const priceUnitRow = useMemo(
    () => findUnitRowByName(effectiveSellingUnit),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [units, effectiveSellingUnit],
  );

  const warehouseDisplayPrice = (field: 'cost' | 'sellingPrice'): string => {
    if (priceUnitRow && priceUnitRow[field]) return priceUnitRow[field];
    const anyCaseRow = units.find((u) => u.unitName && u.baseQty && u[field]);
    if (anyCaseRow) return anyCaseRow[field];
    return '';
  };

  const warehouseUnitNameFor = (_field: 'cost' | 'sellingPrice') => effectiveSellingUnit;

  const effectivePrice = (loc: LocationInventory, field: 'cost' | 'sellingPrice'): string => {
    if (isOverridden(loc.locationId, field)) return loc[field] || '';
    const isWh = isWarehouseLocation(loc.locationId);
    const isBase = !effectiveSellingUnit || effectiveSellingUnit.toLowerCase().trim() === 'each';
    // warehouseDisplayPrice reads from `units` which is filtered to the
    // selected location. Only use it when the row IS the selected location;
    // otherwise the selected location's case-unit price leaks into unrelated
    // warehouse rows.
    const unitsMatchRow = isWh && loc.locationId === selectedLocationId;
    if (unitsMatchRow && !isBase) {
      const whPrice = warehouseDisplayPrice(field);
      if (whPrice) return whPrice;
    }
    if (loc[field]) return loc[field];
    if (unitsMatchRow) return warehouseDisplayPrice(field) || loc[field] || '';
    return '';
  };

  const updateLocationField = (
    locationId: string,
    field: keyof LocationInventory,
    value: any,
    opts?: { syncUnits?: boolean },
  ) => {
    if (field === 'assigned' && locationError) setLocationError('');
    if (opts?.syncUnits && (field === 'cost' || field === 'sellingPrice')) {
      // Store rows are denominated per-each when the selling unit is the
      // base unit, so a price typed there must cascade to the Case/Pack/
      // Pallet rows the same way the Basic Info fields do (per-each ×
      // baseQty). Only the selected location's unit rows live in `units`
      // state, so cascade only when this edit reaches that location —
      // directly, or via the apply-to-all-stores broadcast below.
      const isBaseUnit = !effectiveSellingUnit || effectiveSellingUnit.toLowerCase().trim() === 'each';
      const selectedLoc = locationInventory.find((l) => l.locationId === selectedLocationId);
      const affectsSelected =
        locationId === selectedLocationId ||
        (applyPricesToAllStores &&
          !!selectedLoc &&
          !isWarehouseLocation(selectedLoc.locationId) &&
          !!selectedLoc.assigned &&
          isLocationPricingEditable(selectedLoc.locationId));
      if (isBaseUnit && affectsSelected) {
        // >= 0 so an explicit $0.00 cascades to the unit rows too; NaN
        // (empty input) still skips the sync.
        const perEach = parseFloat(value);
        if (perEach >= 0) {
          setUnits((prev) =>
            prev.map((u) => {
              const qty = parseFloat(u.baseQty);
              return qty > 0 ? { ...u, [field]: (perEach * qty).toFixed(2) } : u;
            }),
          );
        }
      }
    }
    if (field === 'cost' || field === 'sellingPrice') {
      // Mark this cell as explicitly overridden so it stops tracking the
      // default source (form retail for stores, case row for warehouses).
      // Empty value clears the override and the row returns to mirroring
      // the default.
      setPriceOverridden((prev) => {
        const key = `${locationId}::${field}`;
        if (value === '' || value == null) {
          if (!prev.has(key)) return prev;
          const next = new Set(prev);
          next.delete(key);
          return next;
        }
        if (prev.has(key)) return prev;
        const next = new Set(prev);
        next.add(key);
        return next;
      });
      const sourceIsWarehouse = isWarehouseLocation(locationId);
      const broadcastEnabled = sourceIsWarehouse ? applyPricesToAllWarehouses : applyPricesToAllStores;
      if (broadcastEnabled) {
        setLocationOverrides((prev) => {
          const next: Record<string, Partial<LocationInventory>> = { ...prev };
          for (const loc of locationInventory) {
            if (isWarehouseLocation(loc.locationId) !== sourceIsWarehouse) continue;
            if (loc.locationId !== locationId) {
              if (!loc.assigned || !isLocationPricingEditable(loc.locationId)) continue;
            }
            next[loc.locationId] = { ...next[loc.locationId], [field]: value };
          }
          return next;
        });
        if (value !== '' && value != null) {
          setPriceOverridden((prev) => {
            const next = new Set(prev);
            for (const loc of locationInventory) {
              if (isWarehouseLocation(loc.locationId) !== sourceIsWarehouse) continue;
              if (!loc.assigned || !isLocationPricingEditable(loc.locationId)) continue;
              next.add(`${loc.locationId}::${field}`);
            }
            return next;
          });
        }
        return;
      }
    }
    setLocationOverrides((prev) => ({
      ...prev,
      [locationId]: { ...prev[locationId], [field]: value },
    }));
  };

  // Toggle handler for either pool. `kind` selects which kind of locations
  // receive the broadcast; the other pool is left alone. The first eligible
  // assigned row of the chosen kind seeds the values that get propagated.
  const handleApplyPricesToAllToggle = (kind: 'store' | 'warehouse', enabled: boolean) => {
    if (kind === 'warehouse') setApplyPricesToAllWarehouses(enabled);
    else setApplyPricesToAllStores(enabled);
    if (!enabled) return;
    const wantWarehouse = kind === 'warehouse';
    const source = locationInventory.find(
      (l) =>
        l.assigned &&
        isLocationPricingEditable(l.locationId) &&
        isWarehouseLocation(l.locationId) === wantWarehouse &&
        (effectivePrice(l, 'cost') || effectivePrice(l, 'sellingPrice')),
    );
    if (!source) return;
    const sourceCost = effectivePrice(source, 'cost');
    const sourceSelling = effectivePrice(source, 'sellingPrice');
    setLocationOverrides((prev) => {
      const next: Record<string, Partial<LocationInventory>> = { ...prev };
      for (const loc of locationInventory) {
        if (isWarehouseLocation(loc.locationId) !== wantWarehouse) continue;
        if (loc.locationId === source.locationId) continue;
        if (!loc.assigned || !isLocationPricingEditable(loc.locationId)) continue;
        next[loc.locationId] = {
          ...next[loc.locationId],
          cost: sourceCost,
          sellingPrice: sourceSelling,
        };
      }
      return next;
    });
    setPriceOverridden((prev) => {
      const next = new Set(prev);
      for (const loc of locationInventory) {
        if (isWarehouseLocation(loc.locationId) !== wantWarehouse) continue;
        if (!loc.assigned || !isLocationPricingEditable(loc.locationId)) continue;
        if (sourceCost) next.add(`${loc.locationId}::cost`);
        if (sourceSelling) next.add(`${loc.locationId}::sellingPrice`);
      }
      return next;
    });
  };

  // ── Barcode helpers ──
  // Barcodes and SKUs must be alphanumeric only. Scanners and POS lookups can
  // misread anything else, and special chars caused mismatches with imported
  // catalogs in the past, so we strip them at the input layer.
  const sanitizeAlphaNum = (v: string) => v.replace(/[^A-Za-z0-9]/g, '');
  const addBarcode = () => {
    const cleaned = sanitizeAlphaNum(newBarcode);
    if (!cleaned) return;
    // Case-insensitive, matching how the server and the POS scan path compare
    // codes — "ABC123" and "abc123" are the same barcode.
    const already = form.additionalBarcodes.some((b) => b.toLowerCase() === cleaned.toLowerCase());
    if (already || cleaned.toLowerCase() === form.primaryBarcode.trim().toLowerCase()) {
      setAdditionalBarcodeError(`"${cleaned}" is already on this product`);
      return;
    }
    setForm((f) => ({ ...f, additionalBarcodes: [...f.additionalBarcodes, cleaned] }));
    setNewBarcode('');
    setAdditionalBarcodeError('');
  };
  const removeBarcode = (bc: string) => {
    setForm((f) => ({ ...f, additionalBarcodes: f.additionalBarcodes.filter((b) => b !== bc) }));
    setAdditionalBarcodeError('');
  };

  // ── Unit helpers ──
  const addUnit = () => setUnits((u) => [...u, { unitName: '', baseQty: '', sellingPrice: '', cost: '', upc: '' }]);
  const removeUnit = (i: number) => setUnits((u) => u.filter((_, idx) => idx !== i));
  // Edit a unit row by name (case-insensitive). Used by the Basic Info
  // Cost/Selling fields when the active UOM is a multi-pack unit (case,
  // pallet, etc.), so the field binds to that unit row instead of the
  // product-level retail. Creates the row when missing so the user doesn't
  // have to scroll to Case / Pack Pricing first. "each" is the base unit
  // and never lives in product_units, so callers route to the product-
  // level retail field for that case (see writeBasicInfoPrice).
  const updateUnitFieldByName = (unitName: string, field: 'cost' | 'sellingPrice', value: string) => {
    if (!unitName || unitName.toLowerCase().trim() === 'each') return;
    setUnits((prev) => {
      const idx = prev.findIndex((u) => u.unitName.toLowerCase().trim() === unitName.toLowerCase().trim());
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], [field]: value };
        return next;
      }
      return [
        ...prev,
        {
          unitName,
          baseQty: '',
          sellingPrice: field === 'sellingPrice' ? value : '',
          cost: field === 'cost' ? value : '',
          upc: '',
        },
      ];
    });
  };
  const updateUnit = (i: number, field: keyof UnitEntry, value: string) => {
    setUnits((u) => {
      const updated = u.map((entry, idx) => (idx === i ? { ...entry, [field]: value } : entry));
      // Reverse-sync: when a case/pack price changes, recalculate per-each
      if (field === 'sellingPrice' || field === 'cost') {
        const entry = updated[i];
        const casePrice = parseFloat(value);
        const qty = parseFloat(entry.baseQty);
        // >= 0 so a $0.00 case price back-fills the per-each price as well
        if (casePrice >= 0 && qty > 0) {
          const perEach = (casePrice / qty).toFixed(2);
          const priceField = field as 'cost' | 'sellingPrice';
          update(priceField, perEach);
          if (selectedLocationId) {
            updateLocationField(selectedLocationId, priceField, perEach);
          }
        }
      }
      return updated;
    });
  };

  // Unified Basic Info Cost / Selling Price binding used by both store and
  // warehouse contexts so labels like "Cost Price (per case)" mean the
  // same thing wherever they appear. Both fields follow Default Selling
  // Unit (the unit the product is priced in). When that's "each" (the
  // base unit, no productUnits row), reads/writes hit the product-level
  // retail (form.cost / form.sellingPrice). For multi-pack units the
  // field reads from / writes to the matching unit row.
  const writeBasicInfoPrice = (field: 'cost' | 'sellingPrice', value: string) => {
    const unitName = effectiveSellingUnit;
    const isBaseUnit = !unitName || unitName.toLowerCase().trim() === 'each';
    if (selectedLocationId && !isBaseUnit && !isWarehouseLocation(selectedLocationId)) {
      updateLocationField(selectedLocationId, field, value);
      if (value) updateLocationField(selectedLocationId, 'assigned', true);
      return;
    }
    if (isBaseUnit) {
      if (selectedLocationId) {
        updateLocationField(selectedLocationId, field, value);
        update(field, value);
        if (value) updateLocationField(selectedLocationId, 'assigned', true);
      } else {
        update(field, value);
      }
      // Auto-sync case/pack unit prices: per-each × baseQty. >= 0 so an
      // explicit $0.00 propagates; NaN (empty input) still skips the sync.
      const perEach = parseFloat(value);
      if (perEach >= 0) {
        setUnits((prev) =>
          prev.map((u) => {
            const qty = parseFloat(u.baseQty);
            return qty > 0 ? { ...u, [field]: (perEach * qty).toFixed(2) } : u;
          }),
        );
      }
      return;
    }
    updateUnitFieldByName(unitName, field, value);
    if (selectedLocationId && isWarehouseLocation(selectedLocationId)) {
      updateLocationField(selectedLocationId, field, value);
    }
  };
  const readBasicInfoPrice = (field: 'cost' | 'sellingPrice'): string => {
    const unitName = effectiveSellingUnit;
    const isBaseUnit = !unitName || unitName.toLowerCase().trim() === 'each';
    if (selectedLocationId) {
      if (!isBaseUnit && isWarehouseLocation(selectedLocationId)) {
        return warehouseDisplayPrice(field);
      }
      const loc = locationInventory.find((l) => l.locationId === selectedLocationId);
      if (loc) {
        const locVal = effectivePrice(loc, field);
        if (locVal) return locVal;
      }
      if (isBaseUnit) return form[field] || '';
      if (!isWarehouseLocation(selectedLocationId)) return '';
    }
    if (isBaseUnit) {
      return form[field] || '';
    }
    return warehouseDisplayPrice(field);
  };

  // ── Vendor helpers ──
  const addVendor = () => {
    setVendors((v) => [
      ...v,
      {
        vendorId: '',
        vendorItemCode: '',
        vendorProductName: '',
        unitName: 'each',
        vendorPrice: '',
        priceReason: '',
        effectiveDate: new Date().toISOString().slice(0, 10),
      },
    ]);
  };
  const removeVendor = (i: number) => {
    setVendors((v) => v.filter((_, idx) => idx !== i));
    // Keep the display order addressing the same rows: drop i, shift the rest down.
    setVendorOrder((o) => o.filter((idx) => idx !== i).map((idx) => (idx > i ? idx - 1 : idx)));
    setExpandedVendor((cur) => (cur === null || cur === i ? null : cur > i ? cur - 1 : cur));
  };
  const updateVendor = (i: number, field: keyof VendorEntry, value: any) => {
    setVendors((v) => v.map((entry, idx) => (idx === i ? { ...entry, [field]: value } : entry)));
  };

  // ── Vendor display order ──
  // Cheapest first, but recomputed only when a price COMMITS (blur) or the list
  // changes — never on every keystroke. Sorting inline during render, as this tab
  // used to, re-ranked between keystrokes and slid the row out from under the
  // cursor mid-edit.
  const [vendorOrder, setVendorOrder] = useState<number[]>(() => sortVendorIdx(vendors));
  const [expandedVendor, setExpandedVendor] = useState<number | null>(null);
  const [vendorPendingRemoval, setVendorPendingRemoval] = useState<number | null>(null);
  const commitVendorOrder = () => setVendorOrder(sortVendorIdx(vendors));

  /** `vendorOrder` as a guaranteed permutation of the current rows: stale or
   *  duplicate indices are dropped and rows it has not seen yet (a just-added
   *  vendor) fall to the end until the next commit. */
  const vendorDisplayOrder = useMemo(() => {
    const order: number[] = [];
    const used = new Set<number>();
    for (const idx of vendorOrder) {
      if (idx >= 0 && idx < vendors.length && !used.has(idx)) {
        order.push(idx);
        used.add(idx);
      }
    }
    for (let i = 0; i < vendors.length; i++) if (!used.has(i)) order.push(i);
    return order;
  }, [vendorOrder, vendors.length]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear server-driven dedup errors from any prior submit so the user
    // sees the result of the current attempt, not stale state.
    setBarcodeError('');
    setAdditionalBarcodeError('');
    setBarcodeConflict(null);
    setSkuError('');

    if (!form.primaryBarcode?.trim()) {
      setBarcodeError('Primary Barcode (UPC/EAN) is required');
      return;
    }

    if (!form.departmentId) {
      setDepartmentError('Please select a department');
      return;
    }

    // Retail Selling Price requirements:
    // - Stores / "all" context: form.sellingPrice (product-level retail) is
    //   the canonical default — required.
    // - Warehouse context: pricing is per-unit, so we require the unit row
    //   matching Default Selling Unit to have a Selling Price (that's the
    //   row the Basic Info Selling Price field writes to in this mode).
    if (isWarehouseContext) {
      const isBaseUnit = !effectiveSellingUnit || effectiveSellingUnit.toLowerCase().trim() === 'each';
      const whSelling = isBaseUnit
        ? readBasicInfoPrice('sellingPrice') || form.sellingPrice
        : warehouseDisplayPrice('sellingPrice') || readBasicInfoPrice('sellingPrice');
      if (!whSelling || parseFloat(String(whSelling)) < 0) {
        setSellingPriceError(`Selling / Club Price (per ${effectiveSellingUnit}) is required`);
        return;
      }
    } else {
      const isBaseUnit = !effectiveSellingUnit || effectiveSellingUnit.toLowerCase().trim() === 'each';
      const storeSelling = isBaseUnit
        ? selectedLocationId
          ? effectivePrice(locationInventory.find((l) => l.locationId === selectedLocationId)!, 'sellingPrice') ||
            form.sellingPrice
          : form.sellingPrice
        : readBasicInfoPrice('sellingPrice');
      if (!storeSelling || parseFloat(String(storeSelling)) < 0) {
        setSellingPriceError(`Selling / Club Price (per ${effectiveSellingUnit || 'each'}) is required`);
        return;
      }
    }

    const assignedLocations = locationInventory.filter((loc) => loc.assigned);

    if (assignedLocations.length === 0 && locationInventory.length > 0) {
      setLocationError('At least one location must be assigned');
      return;
    }

    if (isWarehouseContext) {
      const warehouseAssigned = assignedLocations.filter((l) => isWarehouseLocation(l.locationId));
      if (warehouseAssigned.length === 0) {
        setLocationError('Assign at least one warehouse');
        return;
      }
      // The case-row Selling Price covers every assigned warehouse (they
      // mirror the same row by design). Already validated above; nothing
      // per-row to check here.
    }

    const locations = assignedLocations.map((loc) => ({
      locationId: loc.locationId,
      inStockQty: parseInt(loc.inStockQty) || 0,
      reorderThreshold: loc.reorderThreshold ? parseInt(loc.reorderThreshold) : null,
      reorderQuantity: loc.reorderQuantity ? parseInt(loc.reorderQuantity) : null,
      // Per-location selling / purchasing unit overrides. Empty string ↔
      // "(use product default)" — the API normalizes empty → NULL so the
      // row falls back to products.default_*_unit on read.
      defaultSellingUnit: loc.defaultSellingUnit || null,
      defaultPurchasingUnit: loc.defaultPurchasingUnit || null,
    }));

    const prices = assignedLocations.map((loc) => {
      // Override on the row wins. When no override is set, fall through to
      // the row's default source: case row for warehouses, form retail for
      // stores. In warehouse-only context we never asked for retail, so
      // the store fallback is null.
      const sellingFallback = isWarehouseContext ? null : form.sellingPrice ? parseFloat(form.sellingPrice) : null;
      const costFallback = isWarehouseContext ? null : form.cost ? parseFloat(form.cost) : null;
      const sellingOverride = isOverridden(loc.locationId, 'sellingPrice') && loc.sellingPrice !== '';
      const costOverride = isOverridden(loc.locationId, 'cost') && loc.cost !== '';
      // A sister location edited by hand (not via an apply-to-all pool) keeps
      // its own stored value for the field the user didn't touch. The default
      // sources below belong to the header location — form retail, and the
      // case row in `units` — so falling through to them would stamp that
      // location's price onto this one.
      if (isEditedSisterPrice(loc.locationId)) {
        const stored = initialData?.prices?.find((p: any) => p.locationId === loc.locationId);
        return {
          locationId: loc.locationId,
          sellingPrice: sellingOverride ? parseFloat(loc.sellingPrice) : toNumberOrNull(stored?.sellingPrice),
          cost: costOverride ? parseFloat(loc.cost) : toNumberOrNull(stored?.cost),
        };
      }
      if (isWarehouseLocation(loc.locationId)) {
        const whSell = warehouseDisplayPrice('sellingPrice');
        const whCost = warehouseDisplayPrice('cost');
        return {
          locationId: loc.locationId,
          sellingPrice: sellingOverride
            ? parseFloat(loc.sellingPrice)
            : whSell
              ? parseFloat(String(whSell))
              : loc.sellingPrice
                ? parseFloat(loc.sellingPrice)
                : sellingFallback,
          cost: costOverride
            ? parseFloat(loc.cost)
            : whCost
              ? parseFloat(String(whCost))
              : loc.cost
                ? parseFloat(loc.cost)
                : costFallback,
        };
      }
      return {
        locationId: loc.locationId,
        sellingPrice: sellingOverride ? parseFloat(loc.sellingPrice) : sellingFallback,
        cost: costOverride ? parseFloat(loc.cost) : costFallback,
      };
    });

    const validUnits = units
      .filter((u) => u.unitName.trim() && u.baseQty)
      .map((u) => ({
        unitName: u.unitName,
        baseQty: parseFloat(u.baseQty),
        sellingPrice: u.sellingPrice ? parseFloat(u.sellingPrice) : null,
        cost: u.cost ? parseFloat(u.cost) : null,
        upc: u.upc || null,
      }));

    // Mirror the server's validateDefaultUnitsAgainstUnits check (mirrors normalizeUnit:
    // case-insensitive + naive trailing-s strip). Without this, the user only learns
    // about the rule via a generic 400 toast that doesn't tell them where to look.
    const norm = (s: string) => {
      const lower = s.toLowerCase().trim();
      return lower.endsWith('s') ? lower.slice(0, -1) : lower;
    };
    const unitsCovers = (target: string) => {
      if (!target || target.toLowerCase() === 'each') return true;
      const t = norm(target);
      return validUnits.some((u) => norm(u.unitName) === t);
    };
    const missing: string[] = [];
    if (!unitsCovers(effectivePurchasingUnit)) missing.push(effectivePurchasingUnit);
    if (!unitsCovers(effectiveSellingUnit) && norm(effectiveSellingUnit) !== norm(effectivePurchasingUnit)) {
      missing.push(effectiveSellingUnit);
    }
    if (missing.length) {
      // Auto-inject empty rows so the user has a clear place to type the baseQty,
      // then surface the inline error pointing at the Units of Measure section.
      const existingNames = new Set(units.map((u) => norm(u.unitName)));
      const additions = missing
        .filter((m) => !existingNames.has(norm(m)))
        .map((m) => ({ unitName: m, baseQty: '', sellingPrice: '', cost: '', upc: '' }));
      if (additions.length) setUnits((prev) => [...prev, ...additions]);
      const msg = `Set the conversion for "${missing.join('", "')}" in the Units of Measure section below — "1 ${missing[0]} = ? each".`;
      setUnitsError(msg);
      setTimeout(() => uomSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
      return;
    }

    // Selling unit must be at or below the purchasing unit on the size
    // hierarchy (each < case < pallet). The useEffect above auto-narrows on
    // change, but a legacy row loaded with an out-of-bounds combo can still
    // reach this point — block submit so it isn't quietly saved.
    if (!form.soldByWeight) {
      const sellLower = effectiveSellingUnit.toLowerCase();
      const purchLower = effectivePurchasingUnit.toLowerCase();
      const sellIdx = (UNIT_OPTIONS as readonly string[]).indexOf(sellLower);
      const purchIdx = (UNIT_OPTIONS as readonly string[]).indexOf(purchLower);
      if (sellIdx >= 0 && purchIdx >= 0 && sellIdx > purchIdx) {
        setUnitsError(
          `Selling unit "${effectiveSellingUnit}" can't be larger than purchasing unit "${effectivePurchasingUnit}".`,
        );
        return;
      }
    }

    // When the purchasing unit is "case" or "pallet" the matching row must
    // also carry a Selling Price — receivers and POs price against that row,
    // so leaving it blank silently breaks the buy-then-resell math. Surface
    // the missing field inline rather than letting the API return a 400.
    // Only require a separate case/pallet Selling Price when the selling unit
    // actually differs from the purchasing unit (i.e. there's a real conversion
    // — buy by the case, sell by the each). When they're the same (buy AND sell
    // by the case), the base/retail Selling Price already prices that unit, so a
    // dedicated case-row Selling Price isn't needed and must not be demanded.
    const purchasingUnit = effectivePurchasingUnit.trim().toLowerCase();
    if (
      (purchasingUnit === 'case' || purchasingUnit === 'pallet') &&
      norm(effectiveSellingUnit) !== norm(effectivePurchasingUnit)
    ) {
      const targetRow = validUnits.find((u) => norm(u.unitName) === norm(purchasingUnit));
      if (!targetRow || !targetRow.sellingPrice || parseFloat(String(targetRow.sellingPrice)) < 0) {
        setUnitsError(
          `A "${purchasingUnit}" conversion exists but is missing a Selling / Club Price. Set the Selling / Club Price on the "${purchasingUnit}" row in the Selling Case / Pack Pricing section below.`,
        );
        return;
      }
    }
    setUnitsError('');

    const validVendors = vendors
      .filter((v) => v.vendorId)
      .map((v) => ({
        payeeId: v.vendorId,
        payeeItemCode: v.vendorItemCode || null,
        vendorProductName: v.vendorProductName || null,
        unitName: v.unitName || 'each',
        vendorPrice: v.vendorPrice ? parseFloat(v.vendorPrice) : null,
        priceReason: v.priceReason || null,
        effectiveDate: v.effectiveDate || null,
      }));

    try {
      await onSubmit({
        name: form.name,
        displayName: form.displayName || null,
        primaryBarcode: form.primaryBarcode || null,
        barcodeSource: form.barcodeSource,
        additionalBarcodes: form.additionalBarcodes.length ? form.additionalBarcodes : [],
        sku: form.sku || null,
        building: form.building || null,
        aisle: form.aisle || null,
        bin: form.bin || null,
        departmentId: form.departmentId || null,
        // POS display order within the department/category. Blank = unranked.
        sortOrder: form.sortOrder.trim() === '' ? null : parseInt(form.sortOrder, 10),
        // Product-level retail price/cost is the store-side default. In
        // warehouse context we never showed those fields, so we omit them
        // from the body — the backend's `if (body.sellingPrice !== undefined)`
        // guard preserves whatever the product had before, leaving the store
        // pricing untouched while we update the warehouse override.
        ...(isWarehouseContext
          ? {}
          : {
              sellingPrice: form.sellingPrice ? parseFloat(form.sellingPrice) : null,
              cashPrice: form.sellingPrice ? parseFloat(form.sellingPrice) : null,
              ...(canSeeCostPrice ? { cost: form.cost ? parseFloat(form.cost) : null } : {}),
            }),
        soldByWeight: form.soldByWeight,
        ageVerification: form.ageVerification,
        ebtEligible: form.ebtEligible,
        excludeEbt: !form.ebtEligible,
        couponEligible: form.couponEligible,
        excludeKitchenPrint: form.excludeKitchenPrint,
        taxable: form.taxable,
        taxExempt: !form.taxable,
        promptQuantity: form.promptQuantity,
        isVariablePrice: form.isVariablePrice,
        crvFee: form.crvFee,
        expiryDate: form.expiryDate || null,
        expiryDate1: form.expiryDate || null,
        batchNo1: form.batchNo1.trim() || null,
        expiryDate2: form.expiryDate2 || null,
        batchNo2: form.batchNo2.trim() || null,
        // With a location: write the store attributes above to it alone, or to
        // every location the user may edit when "Apply to all" is ticked.
        applyToAllLocations: !!selectedLocationId && applyStoreAttrsToAll,
        customField1: form.customField1 || null,
        customField2: form.customField2 || null,
        customField3: form.customField3 || null,
        defaultSellingUnit: form.defaultSellingUnit,
        defaultPurchasingUnit: form.defaultPurchasingUnit,
        // Scope vendor + payee writes to the active location. The backend uses
        // this to replace only the rows owned by `locationId`, leaving other
        // locations' vendor assignments untouched. Empty string falls back to
        // the legacy NULL-location rows so old clients keep working.
        locationId: selectedLocationId || null,
        // Always send arrays (even empty) so the backend interprets the absence
        // of an item as "remove it". `undefined` is treated as "leave alone",
        // which made vendor/category/unit/location removal silently no-op.
        categoryIds: form.categoryIds,
        modifierGroupIds: form.modifierGroupIds,
        locations,
        prices:
          selectedLocationId && initialData?.id
            ? prices.filter((p) => {
                if (p.locationId === selectedLocationId) return true;
                if (inApplyPricesPool(p.locationId)) return true;
                // Sister locations the user typed a price into. Untouched ones
                // are left out so the server keeps their stored prices.
                return isEditedSisterPrice(p.locationId);
              })
            : prices,
        units: validUnits,
        payees: validVendors,
      });
    } catch (err: any) {
      // 409 dedup from the products service. The dashboard's create/edit
      // wrappers rethrow these specifically so we can pin the message to the
      // offending field; everything else they handle with a toast.
      const code = err?.error?.code;
      // DUPLICATE_BARCODE covers the primary barcode AND every additional one,
      // and names which field it landed on. DUPLICATE_PRIMARY_BARCODE is the
      // older code, still sent for one release.
      if (code === 'DUPLICATE_BARCODE' || code === 'DUPLICATE_PRIMARY_BARCODE') {
        const message = err?.error?.message || 'That barcode is already in use';
        setBarcodeConflict(err?.error?.existing ?? null);
        if (err?.error?.field === 'additionalBarcodes') {
          setAdditionalBarcodeError(message);
        } else {
          setBarcodeError(message);
        }
        return;
      }
      if (code === 'DUPLICATE_SKU') {
        setSkuError(err?.error?.message || 'SKU is already in use');
        return;
      }
      throw err;
    }
  };

  const update = (field: string, value: any) => setForm((f) => ({ ...f, [field]: value }));

  const renderOptionToggle = ({ key, label }: { key: string; label: string }) => (
    <label key={key} className="flex items-center justify-between gap-3">
      <span className="text-sm text-foreground">{label}</span>
      <button
        type="button"
        onClick={() => update(key, !form[key as keyof typeof form])}
        className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${form[key as keyof typeof form] ? 'bg-primary' : 'bg-gray-200'}`}
      >
        <span
          className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white transition-transform shadow ${form[key as keyof typeof form] ? 'translate-x-5' : ''}`}
        />
      </button>
    </label>
  );

  // Hint + "Apply to all locations" for the per-store cards. One checkbox
  // drives both cards, matching the single save that carries them.
  const renderStoreAttrsScopeNote = () =>
    selectedLocationId ? (
      <div className="mb-4 space-y-2">
        <p className="text-xs text-muted-foreground">
          Saves to <span className="font-medium text-foreground">{selectedLocationName}</span> only. Other locations
          keep their own value.
        </p>
        <label className="flex items-center gap-2 text-xs font-medium text-foreground">
          <input
            type="checkbox"
            checked={applyStoreAttrsToAll}
            onChange={(e) => setApplyStoreAttrsToAll(e.target.checked)}
            className="h-4 w-4 rounded border-border"
          />
          Apply to all locations
        </label>
      </div>
    ) : (
      <p className="mb-4 text-xs text-muted-foreground">Applies to every location this product is assigned to.</p>
    );

  // Sold-by-weight items always sell as "each" on the POS (the cashier weighs
  // them in-lane and the lb→price math happens server-side). Force the
  // selling unit to "each" whenever the flag is on so a legacy product saved
  // with a non-each selling unit doesn't keep that stale value once the
  // operator flips the toggle. The select is also `disabled` below to make
  // the lock obvious.
  useEffect(() => {
    if (form.soldByWeight && form.defaultSellingUnit !== 'each') {
      setForm((f) => ({ ...f, defaultSellingUnit: 'each' }));
    }
  }, [form.soldByWeight, form.defaultSellingUnit]);

  // Selling unit can never exceed the purchasing unit on the size hierarchy
  // (each < case < pallet) — you can't sell a pallet you only ever bought a
  // case of. When the operator picks a smaller purchasing unit, auto-narrow
  // the selling unit to match. Don't touch sold-by-weight rows; the effect
  // above already pins those to "each".
  useEffect(() => {
    if (form.soldByWeight) return;
    const allowed = allowedSellingUnitsFor(form.defaultPurchasingUnit, form.defaultSellingUnit);
    const current = (form.defaultSellingUnit || 'each').toLowerCase();
    if (!(allowed as readonly string[]).includes(current)) {
      setForm((f) => ({ ...f, defaultSellingUnit: form.defaultPurchasingUnit || 'each' }));
    }
  }, [form.defaultPurchasingUnit, form.defaultSellingUnit, form.soldByWeight]);

  // Location-type unit defaults: a store row with no unit of its own starts at
  // "each", a warehouse row at "case". Seeded only on the header's location —
  // the row the unit dropdowns edit and the save validates (a warehouse "case"
  // asks for its "1 case = ? each" conversion there) — so rows nobody is
  // looking at keep inheriting the product default. Also keeps the row's
  // selling unit at "each" while Sold By Weight is on, as the effect above
  // does for the product-level unit.
  const activeLocationType = selectedLocationId ? locationTypeMap.get(selectedLocationId) : undefined;
  useEffect(() => {
    if (!selectedLocationId || !activeLocationType) return;
    const row = locationInventory.find((l) => l.locationId === selectedLocationId);
    if (!row) return;
    const typeUnit = activeLocationType === 'warehouse' ? 'case' : 'each';
    const purchasing = row.defaultPurchasingUnit || typeUnit;
    const patch: Partial<LocationInventory> = {};
    if (!row.defaultPurchasingUnit) patch.defaultPurchasingUnit = purchasing;
    if (form.soldByWeight) {
      if (row.defaultSellingUnit !== 'each') patch.defaultSellingUnit = 'each';
    } else if (!row.defaultSellingUnit) {
      // Never above the purchasing unit (each < case < pallet).
      patch.defaultSellingUnit = (allowedSellingUnitsFor(purchasing) as readonly string[]).includes(typeUnit)
        ? typeUnit
        : purchasing;
    }
    if (Object.keys(patch).length === 0) return;
    setLocationOverrides((prev) => ({
      ...prev,
      [selectedLocationId]: { ...prev[selectedLocationId], ...patch },
    }));
  }, [selectedLocationId, activeLocationType, locationInventory, form.soldByWeight]);

  const tabs = [
    { id: 'product-pricing', label: 'Product & Pricing' },
    { id: 'vendors', label: 'Vendors' },
    ...(canSeeCostPrice && initialData?.id ? [{ id: 'price-history', label: 'Price History' }] : []),
    ...(canSeeCostPrice && initialData?.id ? [{ id: 'audit-log', label: 'Audit Log' }] : []),
    { id: 'online-store', label: 'Online Store' },
    // Read-only analytics, owners and admins only — saved products only, since
    // there is no history to read before the first save.
    ...(canSeeIntelligence && initialData?.id ? [{ id: 'intelligence', label: 'Intelligence' }] : []),
  ];

  const inputCls =
    'mt-1 h-9 w-full rounded-lg border border-input px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary disabled:cursor-not-allowed disabled:bg-muted/60 disabled:text-muted-foreground';
  const smallInputCls =
    'h-8 w-full rounded-lg border border-input px-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary disabled:cursor-not-allowed disabled:bg-muted/60 disabled:text-muted-foreground';
  // The inventory fields are smallInputCls plus its disabled skin. They no
  // longer carry min-w-[7rem]: at this app's 19px root that was 133px, which is
  // what pushed the old table to 1047px inside a 636px column. The card grid
  // sizes them now.
  const invInputCls = cn(smallInputCls, 'disabled:bg-muted disabled:cursor-not-allowed');
  // Vendor table cell: reads as a table until you reach for it, then behaves as
  // the input it always was. Both hover and focus change the border AND the fill
  // — the ring alone is too weak against the row's hover tint.
  // Hover and focus are drawn with a RING, not a border-color, and the resting
  // border-transparent needs `!`. index.css carries an unlayered
  // `* { border-color: var(--color-border) }`; Tailwind v4 emits utilities into
  // a cascade layer, and unlayered declarations beat layered ones whatever their
  // specificity — so `border-*` utilities here are simply ignored. The
  // transparent 1px border stays so nothing shifts when the ring appears.
  const vendorCellCls =
    'h-9 w-full rounded-md border border-transparent! bg-transparent px-2 text-sm outline-none transition-colors hover:bg-white hover:ring-1 hover:ring-input focus:bg-white focus:ring-1 focus:ring-primary';

  const locationCount = locationInventory.length;

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-scope-spotlight={scopeSpotlight ? 'on' : 'off'}>
      {/* Scope context bar. States the tenant/location rule once, in plain
          words, so the chips further down are read rather than decoded. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-border bg-white px-4 py-2.5">
        {selectedLocationId && selectedLocationName ? (
          <>
            <span className="inline-flex items-center gap-1.5 rounded-md border border-violet-200 bg-violet-50 px-2 py-0.5 text-sm font-semibold text-violet-700">
              <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
              {selectedLocationName}
            </span>
            <span className="text-xs text-muted-foreground">
              Cards marked <span className="font-medium text-violet-700">{selectedLocationName} only</span> save to this
              location. Everything else applies to
              {locationCount > 1 ? ` all ${locationCount} locations` : ' every location'}.
            </span>
          </>
        ) : (
          <span className="text-xs text-muted-foreground">
            <span className="font-medium text-foreground">No location selected.</span> Price, stock and vendor fields
            save the product-wide default. Pick a location in the header to edit a single store.
          </span>
        )}
        <button
          type="button"
          onClick={() => setScopeSpotlight((v) => !v)}
          aria-pressed={scopeSpotlight}
          title="Fade the cards that apply to every location, leaving only the ones this location controls."
          className={`ml-auto inline-flex shrink-0 items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
            scopeSpotlight
              ? 'border-violet-300 bg-violet-50 text-violet-700'
              : 'border-border bg-white text-muted-foreground hover:bg-muted'
          }`}
        >
          <span
            aria-hidden="true"
            className={`relative h-3.5 w-6 shrink-0 rounded-full transition-colors ${
              scopeSpotlight ? 'bg-violet-600' : 'bg-gray-300'
            }`}
          >
            <span
              className={`absolute top-0.5 h-2.5 w-2.5 rounded-full bg-white shadow transition-all ${
                scopeSpotlight ? 'left-3' : 'left-0.5'
              }`}
            />
          </span>
          Spotlight location fields
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-border overflow-x-auto scrollbar-hide">
        <div className="flex w-full min-w-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`relative shrink-0 whitespace-nowrap px-4 py-2.5 text-xs sm:text-sm font-medium transition-colors ${
                activeTab === tab.id ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
              {activeTab === tab.id && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
            </button>
          ))}
        </div>
      </div>

      {!canSaveProduct && (
        <div className="rounded-lg border border-border bg-muted/40 px-4 py-2 text-sm text-muted-foreground">
          You can view this product, but you don&apos;t have permission to edit it.
        </div>
      )}
      {canSaveProduct && lockedParts.length > 0 && (
        <div className="rounded-lg border border-border bg-muted/40 px-4 py-2 text-sm text-muted-foreground">
          You can edit this product, but you don&apos;t have permission to change its{' '}
          {new Intl.ListFormat('en', { type: 'disjunction' }).format(lockedParts)}.
        </div>
      )}

      {/* A disabled fieldset turns every input below read-only for a user who can't save. */}
      <fieldset disabled={!canSaveProduct} className="contents">
        {/* ═══════════════════ PRODUCT & PRICING TAB ═══════════════════ */}
        {activeTab === 'product-pricing' ? (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-6">
              {/* Basic Info */}
              <div className={scopeCardCls('tenant')} data-scope="tenant">
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  <h3 className="text-base font-semibold">Basic Info</h3>
                  <ScopeChip scope="tenant" />
                </div>

                {/* Row 1: Primary Barcode + SKU */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-foreground">
                      Primary Barcode (UPC/EAN) <span className="text-red-500">*</span>
                      {form.barcodeSource === 'auto' && (
                        <span className="ml-2 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                          Auto
                        </span>
                      )}
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={form.primaryBarcode}
                        onChange={(e) => {
                          const cleaned = sanitizeAlphaNum(e.target.value);
                          update('primaryBarcode', cleaned);
                          // Hand-editing a generated code makes it a manual one —
                          // the badge must only ever mean "this exact code came
                          // from the generator".
                          if (form.barcodeSource === 'auto') update('barcodeSource', 'manual');
                          if (cleaned.trim()) setBarcodeError('');
                        }}
                        inputMode="text"
                        autoComplete="off"
                        placeholder="Alphanumeric only"
                        disabled={lockBarcodes}
                        className={`${inputCls} ${barcodeError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                      />
                      {barcodeAutogenActive && !lockBarcodes && (
                        <button
                          type="button"
                          title="Issue the next barcode in this business's series"
                          onClick={async () => {
                            try {
                              const res = await generateBarcode.mutateAsync();
                              update('primaryBarcode', res.data.barcode);
                              update('barcodeSource', 'auto');
                              setBarcodeError('');
                              setBarcodeConflict(null);
                            } catch (err: any) {
                              setBarcodeError(err?.error?.message || 'Could not generate a barcode');
                            }
                          }}
                          disabled={generateBarcode.isPending}
                          className="mt-1 shrink-0 whitespace-nowrap rounded border border-border px-3 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
                        >
                          {generateBarcode.isPending ? 'Generating…' : 'Generate'}
                        </button>
                      )}
                    </div>
                    {barcodeError && (
                      <p className="mt-1 text-xs text-red-500">
                        {barcodeError}
                        {barcodeConflict && (
                          <>
                            {' '}
                            <a href={`/products/${barcodeConflict.id}/edit`} className="underline hover:no-underline">
                              Open {barcodeConflict.name}
                            </a>
                          </>
                        )}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground">SKU</label>
                    <input
                      type="text"
                      value={form.sku}
                      onChange={(e) => {
                        update('sku', sanitizeAlphaNum(e.target.value));
                        if (skuError) setSkuError('');
                      }}
                      inputMode="text"
                      autoComplete="off"
                      placeholder="Alphanumeric only"
                      className={`${inputCls} ${skuError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                    />
                    {skuError && <p className="mt-1 text-xs text-red-500">{skuError}</p>}
                  </div>
                </div>

                {/* Row 2: Additional Barcodes */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-foreground">Additional Barcodes</label>
                  <fieldset disabled={lockBarcodes} className="contents">
                    <div className="mt-2 flex flex-wrap gap-2">
                      {form.additionalBarcodes.map((bc) => (
                        <span
                          key={bc}
                          className="inline-flex max-w-[10rem] items-center gap-1 rounded-full bg-muted px-3 py-1 text-xs font-medium text-foreground"
                        >
                          <span className="truncate">{bc}</span>
                          <button
                            type="button"
                            onClick={() => removeBarcode(bc)}
                            className="text-muted-foreground hover:text-red-500"
                          >
                            &times;
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <input
                        type="text"
                        value={newBarcode}
                        onChange={(e) => {
                          setNewBarcode(sanitizeAlphaNum(e.target.value));
                          if (additionalBarcodeError) setAdditionalBarcodeError('');
                        }}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addBarcode())}
                        inputMode="text"
                        autoComplete="off"
                        placeholder="Alphanumeric barcode — press Enter"
                        className={`h-9 min-w-0 flex-1 rounded-lg border px-3 text-sm outline-none focus:ring-1 disabled:cursor-not-allowed disabled:bg-muted/60 disabled:text-muted-foreground ${
                          additionalBarcodeError
                            ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                            : 'border-input focus:border-primary focus:ring-primary'
                        }`}
                      />
                      <button
                        type="button"
                        onClick={addBarcode}
                        className="inline-flex h-9 shrink-0 items-center gap-1 rounded-lg border border-border px-3 text-sm font-medium text-foreground hover:bg-muted"
                      >
                        <Plus className="h-3 w-3" /> Add
                      </button>
                    </div>
                  </fieldset>
                  {additionalBarcodeError && (
                    <p className="mt-1 text-xs text-red-500">
                      {additionalBarcodeError}
                      {barcodeConflict && (
                        <>
                          {' '}
                          <a href={`/products/${barcodeConflict.id}/edit`} className="underline hover:no-underline">
                            Open {barcodeConflict.name}
                          </a>
                        </>
                      )}
                    </p>
                  )}
                </div>

                {/* Row 3: Product Name */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-foreground">
                    Product Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => update('name', e.target.value)}
                    className={inputCls}
                  />
                </div>

                {/* Row 4: Product Display Name */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-foreground">
                    Product Display Name <span className="text-xs text-muted-foreground">(for receipts / screen)</span>
                  </label>
                  <input
                    type="text"
                    value={form.displayName}
                    onChange={(e) => update('displayName', e.target.value)}
                    placeholder={form.name || 'Same as product name'}
                    className={inputCls}
                  />
                </div>
              </div>

              {/* Cost Calculator — pure UI helper. Now scoped to the active
                location: "Apply to Product" seeds a case row and the form's
                retail Cost/Selling fields, both of which write to whichever
                tier matches selectedLocationId on save (per-location vs
                product-default). Visible in every context so warehouses can
                derive their own case-row pricing too. Lives in a modal that
                opens from the "Open Cost Calculator" button below Product
                Placement. */}
              {canSeeCostPrice && (
                <CostCalculatorModal
                  open={showCostCalculatorModal}
                  onClose={() => setShowCostCalculatorModal(false)}
                  initial={costCalcInitial}
                  onApply={(r) => {
                    writeBasicInfoPrice('cost', r.baseUnitCost.toFixed(2));
                    writeBasicInfoPrice('sellingPrice', r.baseUnitSellingPrice.toFixed(2));
                    if (sellingPriceError) setSellingPriceError('');

                    // Also persist the case → each conversion. Without this, the
                    // calculator's inputs are thrown away on save: only the prices
                    // land on the form, and the next PO for this product has no way
                    // to resolve a case→each baseQty.
                    if (r.unitType !== 'each' && r.unitsInCase > 0) {
                      const lower = r.unitType.toLowerCase();
                      setUnits((prev) => {
                        const existingIdx = prev.findIndex((u) => u.unitName.toLowerCase() === lower);
                        const next = [...prev];
                        const row = {
                          unitName: r.unitType,
                          baseQty: String(r.unitsInCase),
                          // Price for one whole case/pack, not the base unit —
                          // keeps the row consistent with its own unitName.
                          sellingPrice: r.bulkUnitSellingPrice != null ? r.bulkUnitSellingPrice.toFixed(2) : '',
                          cost: r.bulkUnitCost > 0 ? r.bulkUnitCost.toFixed(2) : '',
                          upc: existingIdx >= 0 ? next[existingIdx].upc : '',
                        };
                        if (existingIdx >= 0) next[existingIdx] = row;
                        else next.push(row);
                        return next;
                      });
                      setUnitsError('');
                    }
                    setShowCostCalculatorModal(false);
                  }}
                />
              )}

              {/* Units of Measure            {/* Units of Measure
                The dropdowns are LOCATION-AWARE. When a location is selected
                in the header picker, they read/write the per-location override
                on product_locations.default_*_unit; when no location is
                selected (super-admin / "all" context) they edit the
                tenant-wide products.default_*_unit. The subtitle below makes
                the scope explicit. */}
              {(() => {
                const activeLoc = selectedLocationId
                  ? locationInventory.find((l) => l.locationId === selectedLocationId)
                  : null;
                const showingLocationOverride = !!activeLoc;
                const sellingValue = showingLocationOverride
                  ? activeLoc!.defaultSellingUnit || form.defaultSellingUnit || 'each'
                  : form.defaultSellingUnit;
                const purchasingValue = showingLocationOverride
                  ? activeLoc!.defaultPurchasingUnit || form.defaultPurchasingUnit || 'each'
                  : form.defaultPurchasingUnit;
                const ensureMatchingUnitRow = (unitName: string) => {
                  const norm = (s: string) => (s || '').toLowerCase().trim();
                  if (!unitName || norm(unitName) === 'each') return;
                  const seedSelling = form.sellingPrice || '';
                  const seedCost = form.cost || '';
                  setUnits((prev) => {
                    const idx = prev.findIndex((u) => norm(u.unitName) === norm(unitName));
                    if (idx >= 0) {
                      const row = prev[idx];
                      const needsBaseQty = !row.baseQty;
                      const needsSelling = !row.sellingPrice && seedSelling;
                      const needsCost = !row.cost && seedCost;
                      if (!needsBaseQty && !needsSelling && !needsCost) return prev;
                      const next = [...prev];
                      next[idx] = {
                        ...row,
                        baseQty: row.baseQty || '1',
                        sellingPrice: row.sellingPrice || seedSelling,
                        cost: row.cost || seedCost,
                      };
                      return next;
                    }
                    return [...prev, { unitName, baseQty: '1', sellingPrice: seedSelling, cost: seedCost, upc: '' }];
                  });
                };
                const onSellingChange = (value: string) => {
                  if (showingLocationOverride) {
                    updateLocationField(activeLoc!.locationId, 'defaultSellingUnit', value);
                  }
                  update('defaultSellingUnit', value);
                  setUnitsError('');
                  ensureMatchingUnitRow(value);
                };
                const onPurchasingChange = (value: string) => {
                  if (showingLocationOverride) {
                    updateLocationField(activeLoc!.locationId, 'defaultPurchasingUnit', value);
                  }
                  update('defaultPurchasingUnit', value);
                  setUnitsError('');
                  const norm = (s: string) => (s || '').toLowerCase().trim();
                  if (norm(value) !== 'each' && norm(value) === norm(sellingValue)) {
                    ensureMatchingUnitRow(value);
                  }
                };
                return (
                  <div ref={uomSectionRef} className={scopeCardCls(activeScope)} data-scope={activeScope}>
                    <div className="mb-4 flex items-baseline justify-between gap-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-base font-semibold">Units of Measure</h3>
                        <ScopeChip scope={activeScope} locationName={selectedLocationName} />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {showingLocationOverride ? (
                          <>
                            Editing units for{' '}
                            <span className="font-medium text-foreground">{activeLoc!.locationName}</span> — switch the
                            location picker to edit a different store/warehouse, or clear the picker to edit the
                            tenant-wide default.
                          </>
                        ) : (
                          <>Editing the tenant-wide default. Pick a location in the header to override per-location.</>
                        )}
                      </p>
                    </div>
                    <div
                      className={`grid grid-cols-1 gap-4 ${
                        canSeeCostPrice ? 'sm:grid-cols-[1fr_1fr_auto] sm:items-end' : 'sm:grid-cols-2'
                      }`}
                    >
                      <div>
                        <label className="block text-sm font-medium text-foreground">
                          <ScopeDot className="mr-1.5 align-middle" />
                          Purchasing Unit
                          {showingLocationOverride && !activeLoc!.defaultPurchasingUnit && (
                            <span className="ml-1 text-xs font-normal text-muted-foreground">
                              (inheriting product default: {form.defaultPurchasingUnit || 'each'})
                            </span>
                          )}
                        </label>
                        <select
                          value={purchasingValue}
                          onChange={(e) => onPurchasingChange(e.target.value)}
                          className="mt-1 h-9 w-full rounded-lg border border-input px-3 text-sm"
                        >
                          {unitOptionsFor(purchasingValue).map((u) => (
                            <option key={u} value={u}>
                              {u}
                            </option>
                          ))}
                        </select>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {unitScopeHint(scopeSpread.purchasingUnit)}
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground">
                          <ScopeDot className="mr-1.5 align-middle" />
                          Selling Unit
                          {form.soldByWeight && (
                            <span className="ml-1 text-xs font-normal text-muted-foreground">
                              (locked to "each" while Sold By Weight is on)
                            </span>
                          )}
                          {!form.soldByWeight && showingLocationOverride && !activeLoc!.defaultSellingUnit && (
                            <span className="ml-1 text-xs font-normal text-muted-foreground">
                              (inheriting product default: {form.defaultSellingUnit || 'each'})
                            </span>
                          )}
                        </label>
                        <select
                          value={sellingValue}
                          onChange={(e) => onSellingChange(e.target.value)}
                          disabled={form.soldByWeight}
                          title={
                            form.soldByWeight
                              ? 'Selling unit is locked to "each" because Sold By Weight is on.'
                              : `Selling unit must be at or below the purchasing unit (${purchasingValue}).`
                          }
                          className="mt-1 h-9 w-full rounded-lg border border-input px-3 text-sm disabled:bg-muted disabled:cursor-not-allowed"
                        >
                          {allowedSellingUnitsFor(purchasingValue, sellingValue).map((u) => (
                            <option key={u} value={u}>
                              {u}
                            </option>
                          ))}
                        </select>
                        <p className="mt-1 text-xs text-muted-foreground">{unitScopeHint(scopeSpread.sellingUnit)}</p>
                      </div>
                      {canSeeCostPrice && (
                        <div
                          aria-hidden
                          className="hidden sm:inline-flex h-9 shrink-0 items-center gap-1 self-end rounded-lg border border-transparent px-3 text-sm font-medium invisible"
                        >
                          <Calculator className="h-3.5 w-3.5" />
                          Cost Calculator
                        </div>
                      )}
                    </div>
                    {(() => {
                      const costUnit = warehouseUnitNameFor('cost');
                      const sellUnit = warehouseUnitNameFor('sellingPrice');
                      const suffix = (u: string) => (u ? ` (per ${u})` : '');
                      // Top-aligned (not items-end) so the margin / error text under
                      // Selling Price hangs below the row instead of pushing its input up.
                      return (
                        <div
                          className={`mt-4 grid grid-cols-1 gap-4 ${
                            canSeeCostPrice ? 'sm:grid-cols-[1fr_1fr_auto] sm:items-start' : ''
                          }`}
                        >
                          {canSeeCostPrice && (
                            <div>
                              <label className="block text-sm font-medium text-foreground">
                                Cost Price{suffix(costUnit)}
                              </label>
                              <input
                                type="number"
                                inputMode="decimal"
                                step="0.01"
                                min="0"
                                value={readBasicInfoPrice('cost')}
                                onChange={(e) => writeBasicInfoPrice('cost', e.target.value)}
                                placeholder={selectedLocationId ? 'N/A — no price for this location' : '0.00'}
                                className={inputCls}
                              />
                            </div>
                          )}
                          <div>
                            <label className="block text-sm font-medium text-foreground">
                              Selling / Club Price{suffix(sellUnit)} <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="number"
                              inputMode="decimal"
                              step="0.01"
                              min="0"
                              value={readBasicInfoPrice('sellingPrice')}
                              onChange={(e) => {
                                writeBasicInfoPrice('sellingPrice', e.target.value);
                                if (sellingPriceError) setSellingPriceError('');
                              }}
                              placeholder={selectedLocationId ? 'N/A — no price for this location' : '0.00'}
                              disabled={lockPrices}
                              className={`${inputCls} ${sellingPriceError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                            />
                            {sellingPriceError && <p className="mt-1 text-xs text-red-500">{sellingPriceError}</p>}
                            {canSeeMargin &&
                              (() => {
                                const cost = parseFloat(readBasicInfoPrice('cost'));
                                const price = parseFloat(readBasicInfoPrice('sellingPrice'));
                                const margin = grossMarginPct(cost, price);
                                if (margin == null) return null;
                                const per = sellUnit || 'each';
                                const profit = price - cost;
                                return margin < 0 ? (
                                  <p className="mt-1 text-xs text-red-500" title="Selling price is lower than cost">
                                    Selling <span className="font-medium tabular-nums">{formatCurrency(-profit)}</span>{' '}
                                    below cost per {per} ({margin.toFixed(1)}% margin)
                                  </p>
                                ) : (
                                  <p
                                    className="mt-1 text-xs text-muted-foreground"
                                    title="Gross margin = (selling price − cost) ÷ selling price"
                                  >
                                    <span className="font-medium tabular-nums text-foreground">
                                      {margin.toFixed(1)}%
                                    </span>{' '}
                                    gross margin — you earn{' '}
                                    <span className="font-medium tabular-nums text-foreground">
                                      {formatCurrency(profit)}
                                    </span>{' '}
                                    on each {per} sold
                                  </p>
                                );
                              })()}
                            {/* What the other locations charge. Turns "this is
                              location-scoped" from a rule into a fact about
                              this product — the thing that actually tells the
                              user whether the scope matters here. */}
                            {selectedLocationId && scopeSpread.priceRange && scopeSpread.sellingPrice.distinct > 1 && (
                              <p className="mt-1 text-xs text-violet-700">
                                Across {scopeSpread.total} locations:{' '}
                                <span className="font-medium tabular-nums">
                                  {formatCurrency(scopeSpread.priceRange.min)}
                                </span>{' '}
                                –{' '}
                                <span className="font-medium tabular-nums">
                                  {formatCurrency(scopeSpread.priceRange.max)}
                                </span>
                              </p>
                            )}
                          </div>
                          {canSeeCostPrice && (
                            <button
                              type="button"
                              onClick={() => setShowCostCalculatorModal(true)}
                              // sm:mt-6 = label line (text-sm) + the input's mt-1, so the button
                              // lines up with the inputs rather than their labels.
                              className="inline-flex h-9 shrink-0 items-center gap-1 rounded-lg border border-border px-3 text-sm font-medium text-foreground hover:bg-muted sm:mt-6"
                            >
                              <Calculator className="h-3.5 w-3.5" />
                              Cost Calculator
                            </button>
                          )}
                        </div>
                      );
                    })()}
                    {unitsError && (
                      <p
                        className="mt-3 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900"
                        role="alert"
                      >
                        {unitsError}
                      </p>
                    )}
                    {purchasingValue === 'each' && (
                      <p className="mt-3 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                        <strong>Heads up:</strong> Purchasing Unit is "each" — POs for this product will use a 1:1
                        conversion. If you actually buy this product in cases / packs / pallets, switch the dropdown
                        above and add the matching unit row in "Case / Pack Pricing" so receive credits the correct
                        stock.
                      </p>
                    )}
                  </div>
                );
              })()}

              {/* Case/Pack Units */}
              <div className={scopeCardCls(activeScope)} data-scope={activeScope}>
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-base font-semibold">Selling / Club Price as Case / Pack / Pallet etc.,</h3>
                    <ScopeChip scope={activeScope} locationName={selectedLocationName} />
                  </div>
                  <button
                    type="button"
                    onClick={addUnit}
                    className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
                  >
                    <Plus className="h-3 w-3" /> Add Unit
                  </button>
                </div>
                <p className="mb-3 text-xs text-muted-foreground">
                  Individual price above is for retail customers. Case/pack pricing below is for bulk buyers
                  (restaurants, etc.). Each case with a barcode is scannable as a separate item at POS.
                </p>
                {units.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No case/pack units. Click "+ Add Unit" to set bulk pricing.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {units.map((unit, i) => (
                      <div key={i} className="rounded-lg border border-border p-3 bg-background space-y-3">
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground">Unit Name</label>
                            <select
                              value={unit.unitName}
                              onChange={(e) => updateUnit(i, 'unitName', e.target.value)}
                              className={smallInputCls}
                            >
                              <option value="">Select unit</option>
                              {unitOptionsFor(unit.unitName).map((u) => (
                                <option key={u} value={u}>
                                  {u}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground">
                              # each ({unit.unitName || 'unit'} → each)
                            </label>
                            <input
                              type="number"
                              step="1"
                              min="1"
                              value={unit.baseQty}
                              onChange={(e) => updateUnit(i, 'baseQty', e.target.value)}
                              placeholder="24"
                              className={smallInputCls}
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground">Barcode (UPC)</label>
                            <input
                              type="text"
                              value={unit.upc}
                              onChange={(e) => updateUnit(i, 'upc', e.target.value)}
                              placeholder="Optional"
                              disabled={lockBarcodes}
                              className={smallInputCls}
                            />
                          </div>
                        </div>
                        <div
                          className={`grid grid-cols-1 gap-3 sm:grid-cols-2 ${canSeeCostPrice ? 'md:grid-cols-3' : ''}`}
                        >
                          {canSeeCostPrice && (
                            <div>
                              <label className="block text-xs font-medium text-muted-foreground">
                                Cost Price (per {unit.unitName || 'unit'})
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={unit.cost}
                                onChange={(e) => updateUnit(i, 'cost', e.target.value)}
                                placeholder="0.00"
                                className={smallInputCls}
                              />
                            </div>
                          )}
                          <div>
                            <label className="block text-xs font-medium text-muted-foreground">
                              Selling / Club Price (per {unit.unitName || 'unit'})
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={unit.sellingPrice}
                              onChange={(e) => updateUnit(i, 'sellingPrice', e.target.value)}
                              placeholder="0.00"
                              disabled={lockPrices}
                              className={smallInputCls}
                            />
                          </div>
                          <div className="flex items-end gap-2">
                            {unit.baseQty && unit.sellingPrice ? (
                              <div className="flex-1 space-y-1">
                                <div className="hidden">
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Individual Unit Price:</span>
                                    <span className="font-semibold text-foreground">
                                      ${(parseFloat(unit.sellingPrice) / parseFloat(unit.baseQty)).toFixed(2)}
                                    </span>
                                  </div>
                                  {canSeeCostPrice && unit.cost && (
                                    <div className="flex justify-between">
                                      <span className="text-muted-foreground">Individual Unit Cost Price:</span>
                                      <span className="font-semibold text-foreground">
                                        ${(parseFloat(unit.cost) / parseFloat(unit.baseQty)).toFixed(2)}
                                      </span>
                                    </div>
                                  )}
                                </div>
                                {(!form.sellingPrice || !form.cost) && (
                                  <div className="flex items-center justify-between gap-2">
                                    <p className="text-[10px] text-muted-foreground">
                                      Use calculated unit price as the product's retail price.
                                    </p>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const qty = parseFloat(unit.baseQty);
                                        if (!qty || qty <= 0) return;
                                        const updates: Record<string, string> = {};
                                        if (!form.sellingPrice && unit.sellingPrice) {
                                          const val = parseFloat(unit.sellingPrice) / qty;
                                          if (val > 0) updates.sellingPrice = val.toFixed(2);
                                        }
                                        if (!form.cost && unit.cost) {
                                          const val = parseFloat(unit.cost) / qty;
                                          if (val > 0) updates.cost = val.toFixed(2);
                                        }
                                        setForm((f) => ({ ...f, ...updates }));
                                      }}
                                      className="shrink-0 rounded border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/20"
                                    >
                                      Apply
                                    </button>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="flex-1" />
                            )}
                            <button
                              type="button"
                              onClick={() => removeUnit(i)}
                              className="shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Location Inventory — split into two sibling cards (Stores
                and Warehouses) so the two pricing pools stay visually
                separate. The off-context kind shows but is read-only. */}
              {(() => {
                const renderApplyPricesToggle = (kind: 'store' | 'warehouse') => {
                  // Pool eligibility ignores form context: when 2+ locations of
                  // this kind are permission-accessible, surface the toggle in
                  // both the editable card and (as a read-only indicator) the
                  // off-context sibling card so the user can see at a glance
                  // whether prices are synced across the pool.
                  const isKind = (l: LocationInventory) => (kind === 'warehouse') === isWarehouseLocation(l.locationId);
                  const permitted = locationInventory.filter(
                    (l) =>
                      isKind(l) &&
                      canEditLocation(l.locationId) &&
                      !(isWarehouseLocation(l.locationId) && !canEditWarehouseInventory),
                  );
                  const show =
                    permitted.length > 1 &&
                    permitted.some(
                      (l) => l.assigned && (effectivePrice(l, 'cost') || effectivePrice(l, 'sellingPrice')),
                    );
                  if (!show) return null;
                  // Toggle becomes a read-only indicator when its pool is the
                  // off-context kind (e.g. warehouses while a store is the
                  // active header location). The user can still see the current
                  // state but can't change it from this side.
                  const interactive = permitted.some((l) => !isOffContextRow(l.locationId));
                  const checked = kind === 'warehouse' ? applyPricesToAllWarehouses : applyPricesToAllStores;
                  const label =
                    kind === 'warehouse' ? (
                      <>
                        Apply same prices to <span className="font-bold text-orange-600">selected</span> warehouses
                      </>
                    ) : (
                      <>
                        Apply same prices to <span className="font-bold text-orange-600">selected</span> stores
                      </>
                    );
                  const description =
                    kind === 'warehouse'
                      ? 'Cost & Selling / Club Price stay in sync across all assigned warehouse locations. Stores are unaffected.'
                      : 'Cost & Selling / Club Price stay in sync across all assigned store locations. Warehouses are unaffected.';
                  const lockTitle = interactive
                    ? undefined
                    : kind === 'warehouse'
                      ? 'Switch the header location to a warehouse to change this.'
                      : 'Switch the header location to a store to change this.';
                  return (
                    <div
                      className="mb-4 flex items-start justify-between gap-4 rounded-lg border border-border bg-muted/30 px-4 py-3"
                      title={lockTitle}
                    >
                      <div>
                        <div className="text-sm font-medium">
                          {label}
                          {!interactive && (
                            <span className="ml-2 inline-flex items-center rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                              read-only
                            </span>
                          )}
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground">{description}</div>
                      </div>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={checked}
                        disabled={!interactive}
                        onClick={() => interactive && handleApplyPricesToAllToggle(kind, !checked)}
                        title={lockTitle}
                        className={`inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-all ${
                          checked
                            ? 'bg-primary border-primary justify-end pr-0.5'
                            : 'bg-muted border-border justify-start pl-0.5'
                        } ${!interactive ? 'opacity-60 cursor-not-allowed' : ''}`}
                      >
                        <span
                          className={`inline-block h-5 w-5 rounded-full shadow-sm transition-all ${
                            checked ? 'bg-white' : 'bg-background'
                          }`}
                        />
                      </button>
                    </div>
                  );
                };

                const renderInventorySection = (kind: 'store' | 'warehouse') => {
                  const isWarehouseSection = kind === 'warehouse';
                  const sectionRows = locationInventory.filter(
                    (l) => isWarehouseLocation(l.locationId) === isWarehouseSection,
                  );
                  const title = isWarehouseSection ? 'Warehouse Inventory' : 'Store Inventory';
                  const description = isWarehouseSection
                    ? 'Assign product to warehouses and set per-warehouse price overrides and stock quantities.'
                    : 'Assign product to stores and set per-store price overrides and stock quantities.';
                  // A scoped user with no location of this kind isn't looking at
                  // an unconfigured tenant — the rows exist, they just aren't
                  // theirs. Telling them to go add one would send them to a page
                  // they can't use.
                  const emptyMessage = isWarehouseSection
                    ? locationsLoading
                      ? 'Loading warehouses...'
                      : canEditAllLocations
                        ? 'No warehouses configured. Add warehouses in POS Management > Locations.'
                        : 'No warehouses assigned to you.'
                    : locationsLoading
                      ? 'Loading stores...'
                      : canEditAllLocations
                        ? 'No stores configured. Add stores in POS Management > Locations.'
                        : 'No stores assigned to you.';
                  const editableRows = sectionRows.filter(
                    (l) => canEditLocation(l.locationId) && !isOffContextRow(l.locationId),
                  );
                  const sectionLocked =
                    sectionRows.length > 0 && sectionRows.every((l) => isOffContextRow(l.locationId));
                  // Resolve the unit shown in this section's column headers.
                  // Using `form.defaultSellingUnit` everywhere leaks the active
                  // location's unit (e.g. warehouse "case") into the off-
                  // context section. Strategy:
                  //   1. Trust per-row `defaultSellingUnit` overrides — that's
                  //      the ground truth for these rows. If every row in the
                  //      section agrees, use that unit.
                  //   2. If rows disagree, omit the unit suffix (we can't pick
                  //      one without misleading the reader).
                  //   3. If no row carries an explicit override, fall back to
                  //      the form selector — but only for the active section,
                  //      since that's what the user sees in the Selling Unit
                  //      dropdown. Off-context sections stay generic so the
                  //      active unit can't bleed across the boundary.
                  const isActiveSection =
                    (isWarehouseSection && formContext === 'warehouse') ||
                    (!isWarehouseSection && formContext === 'store') ||
                    formContext === 'all';
                  const rowUnits = Array.from(
                    new Set(sectionRows.map((r) => r.defaultSellingUnit).filter((u): u is string => !!u)),
                  );
                  const sectionUnit =
                    rowUnits.length === 1
                      ? rowUnits[0]
                      : rowUnits.length > 1
                        ? ''
                        : isActiveSection
                          ? form.defaultSellingUnit || ''
                          : '';
                  return (
                    <div className={scopeCardCls('location')} data-scope="location">
                      <div className="mb-1 flex flex-wrap items-center gap-2">
                        <MapPin className={`h-4 w-4 ${isWarehouseSection ? 'text-orange-600' : 'text-primary'}`} />
                        <h3 className="text-base font-semibold">{title}</h3>
                        {sectionLocked && (
                          <span className="inline-flex items-center rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                            {isWarehouseSection && !canEditWarehouseInventory ? 'read-only' : 'prices only'} — selected
                            location is {isWarehouseSection ? 'not warehouse' : 'warehouse'}
                          </span>
                        )}
                      </div>
                      <p className="mb-4 text-xs text-muted-foreground">{description}</p>
                      {locationError &&
                        (isWarehouseSection
                          ? locationError.toLowerCase().includes('warehouse')
                          : !locationError.toLowerCase().includes('warehouse')) && (
                          <p className="mb-4 text-xs text-red-500">{locationError}</p>
                        )}

                      {renderApplyPricesToggle(kind)}

                      {sectionRows.length === 0 ? (
                        <div className="text-sm text-muted-foreground">{emptyMessage}</div>
                      ) : (
                        <div className="space-y-3">
                          {/* Select-all was the checkbox in the table's header cell.
                            Cards have no header row, so it becomes a labelled
                            control of its own rather than quietly disappearing —
                            same handler, same disabled rule. */}
                          <label className="flex w-fit items-center gap-2 text-xs font-medium text-muted-foreground">
                            <input
                              type="checkbox"
                              checked={editableRows.length > 0 && editableRows.every((l) => l.assigned)}
                              onChange={(e) => {
                                for (const loc of editableRows) {
                                  updateLocationField(loc.locationId, 'assigned', e.target.checked);
                                }
                              }}
                              disabled={editableRows.length === 0}
                              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
                            />
                            Assign all {isWarehouseSection ? 'warehouses' : 'stores'}
                          </label>

                          {sectionRows.map((loc) => {
                            // Lock every field on this card when the user doesn't
                            // have permission for this location, or the row's kind
                            // doesn't match the active form context. Warehouse rows
                            // are also read-only for non-managerial roles regardless
                            // of location grants.
                            const warehouseLocked = isWarehouseSection && !canEditWarehouseInventory;
                            const offContextLocked = isOffContextRow(loc.locationId);
                            const editable = canEditLocation(loc.locationId) && !warehouseLocked && !offContextLocked;
                            // Cost / Selling Price stay editable on off-context
                            // rows: a price is that row's own productPrices entry,
                            // so it can't bleed across the store/warehouse line.
                            // Stock, thresholds and assignment stay locked.
                            const priceEditable = canEditLocation(loc.locationId) && !warehouseLocked;
                            const priceInputDisabled = !priceEditable || !loc.assigned || lockPrices;
                            const lockTitle = editable
                              ? undefined
                              : offContextLocked
                                ? isWarehouseSection
                                  ? 'Switch the header location to a warehouse to edit warehouse inventory.'
                                  : 'Switch the header location to a store to edit store inventory.'
                                : warehouseLocked
                                  ? 'Warehouse pricing & inventory can only be edited by owner / admin / manager.'
                                  : "Read-only — you don't have edit permission for this location.";
                            const inputDisabled = !editable || !loc.assigned;
                            // Labels are real <label for>, so each field needs an id
                            // unique across both sections on the page.
                            const fieldId = (field: string) => `inv-${kind}-${loc.locationId}-${field}`;
                            return (
                              <div
                                key={loc.locationId}
                                title={lockTitle}
                                className={cn(
                                  'rounded-lg border border-border',
                                  (!loc.assigned || !priceEditable) && 'opacity-60',
                                )}
                              >
                                <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/50 px-4 py-2.5">
                                  <input
                                    type="checkbox"
                                    checked={loc.assigned}
                                    onChange={(e) => updateLocationField(loc.locationId, 'assigned', e.target.checked)}
                                    disabled={!editable}
                                    title={lockTitle}
                                    aria-label={`Assign ${loc.locationName}`}
                                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
                                  />
                                  {loc.locationId === selectedLocationId && (
                                    <span
                                      title="Currently selected location"
                                      className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
                                        isWarehouseSection
                                          ? 'bg-orange-100 text-orange-600'
                                          : 'bg-primary/10 text-primary'
                                      }`}
                                    >
                                      <MapPin className="h-3.5 w-3.5" />
                                    </span>
                                  )}
                                  <span className="text-sm font-semibold text-foreground">{loc.locationName}</span>
                                  {/* The section lists every store/warehouse, so the
                                    "only" chip goes on the card being edited from
                                    the header, not on the section title. */}
                                  {loc.locationId === selectedLocationId && (
                                    <ScopeChip scope="location" locationName={loc.locationName} />
                                  )}
                                  {!editable && (
                                    <span className="inline-flex items-center rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                                      {priceEditable ? 'prices only' : 'read-only'}
                                    </span>
                                  )}
                                </div>

                                <div className="grid grid-cols-1 gap-x-4 gap-y-3 px-4 py-3 sm:grid-cols-2 md:grid-cols-3">
                                  {canSeeCostPrice && (
                                    <div>
                                      <label htmlFor={fieldId('cost')} className={INV_FIELD_LABEL}>
                                        Cost Price{sectionUnit ? ` (per ${sectionUnit})` : ''}
                                      </label>
                                      <input
                                        id={fieldId('cost')}
                                        type="number"
                                        step="0.01"
                                        value={effectivePrice(loc, 'cost')}
                                        onChange={(e) =>
                                          updateLocationField(loc.locationId, 'cost', e.target.value, {
                                            syncUnits: !isWarehouseSection,
                                          })
                                        }
                                        disabled={priceInputDisabled}
                                        title={
                                          !priceEditable
                                            ? lockTitle
                                            : offContextLocked
                                              ? 'Type to set cost for this location.'
                                              : !isOverridden(loc.locationId, 'cost')
                                                ? isWarehouseSection
                                                  ? `Synced with the "${form.defaultSellingUnit}" case row Cost. Type to override.`
                                                  : 'Type to set cost for this location.'
                                                : undefined
                                        }
                                        placeholder={
                                          isWarehouseSection && loc.locationId === selectedLocationId
                                            ? String(warehouseDisplayPrice('cost') || '—')
                                            : '—'
                                        }
                                        className={invInputCls}
                                      />
                                    </div>
                                  )}
                                  <div>
                                    <label htmlFor={fieldId('selling')} className={INV_FIELD_LABEL}>
                                      Selling / Club Price{sectionUnit ? ` (per ${sectionUnit})` : ''}{' '}
                                      <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                      id={fieldId('selling')}
                                      type="number"
                                      step="0.01"
                                      value={effectivePrice(loc, 'sellingPrice')}
                                      onChange={(e) =>
                                        updateLocationField(loc.locationId, 'sellingPrice', e.target.value, {
                                          syncUnits: !isWarehouseSection,
                                        })
                                      }
                                      disabled={priceInputDisabled}
                                      title={
                                        !priceEditable
                                          ? lockTitle
                                          : offContextLocked
                                            ? 'Type to set selling price for this location.'
                                            : !isOverridden(loc.locationId, 'sellingPrice')
                                              ? isWarehouseSection
                                                ? `Synced with the "${form.defaultSellingUnit}" case row Selling / Club Price. Type to override.`
                                                : 'Type to set selling price for this location.'
                                              : undefined
                                      }
                                      placeholder={
                                        isWarehouseSection && loc.locationId === selectedLocationId
                                          ? String(warehouseDisplayPrice('sellingPrice') || '—')
                                          : '—'
                                      }
                                      className={invInputCls}
                                    />
                                  </div>
                                  <div>
                                    <label htmlFor={fieldId('stock')} className={INV_FIELD_LABEL}>
                                      In Stock Qty
                                    </label>
                                    <input
                                      id={fieldId('stock')}
                                      type="number"
                                      step="1"
                                      value={loc.inStockQty}
                                      onChange={(e) =>
                                        updateLocationField(loc.locationId, 'inStockQty', e.target.value)
                                      }
                                      disabled={inputDisabled}
                                      title={lockTitle}
                                      className={invInputCls}
                                    />
                                  </div>
                                  <div>
                                    <label htmlFor={fieldId('threshold')} className={INV_FIELD_LABEL}>
                                      Reorder Threshold
                                    </label>
                                    <input
                                      id={fieldId('threshold')}
                                      type="number"
                                      step="1"
                                      value={loc.reorderThreshold}
                                      onChange={(e) =>
                                        updateLocationField(loc.locationId, 'reorderThreshold', e.target.value)
                                      }
                                      disabled={inputDisabled}
                                      title={lockTitle}
                                      placeholder="—"
                                      className={invInputCls}
                                    />
                                  </div>
                                  <div>
                                    <label htmlFor={fieldId('reorderQty')} className={INV_FIELD_LABEL}>
                                      Reorder Quantity
                                    </label>
                                    <input
                                      id={fieldId('reorderQty')}
                                      type="number"
                                      step="1"
                                      value={loc.reorderQuantity}
                                      onChange={(e) =>
                                        updateLocationField(loc.locationId, 'reorderQuantity', e.target.value)
                                      }
                                      disabled={inputDisabled}
                                      title={lockTitle}
                                      placeholder="—"
                                      className={invInputCls}
                                    />
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                };

                return (
                  <>
                    {renderInventorySection('store')}
                    {renderInventorySection('warehouse')}
                    <PrivateWarehouseSection productId={initialData?.id} locationIds={privateWarehouseLocationIds} />
                  </>
                );
              })()}
            </div>

            {/* Right column: Department, Product Options, Item Placement */}
            <div className="space-y-6">
              {/* Department + Product Categories */}
              <div className={scopeCardCls('tenant')} data-scope="tenant">
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  <h3 className="text-base font-semibold">
                    Department <span className="text-red-500">*</span>
                  </h3>
                  <ScopeChip scope="tenant" />
                </div>
                <select
                  value={form.departmentId}
                  onChange={(e) => {
                    const deptId = e.target.value;
                    update('departmentId', deptId);
                    setDepartmentError('');
                    // Auto-apply department defaults to product options
                    const dept = departments.find((d: any) => d.id === deptId);
                    if (dept) {
                      setForm((f) => ({
                        ...f,
                        departmentId: deptId,
                        taxable: dept.defaultTaxable ?? f.taxable,
                        ebtEligible: dept.defaultEbtEligible ?? f.ebtEligible,
                        couponEligible: dept.defaultCouponEligible ?? f.couponEligible,
                        ageVerification: dept.defaultAgeVerification ?? f.ageVerification,
                        excludeKitchenPrint: dept.defaultExcludeKitchenPrint ?? f.excludeKitchenPrint,
                        soldByWeight: dept.defaultSoldByWeight ?? f.soldByWeight,
                        promptQuantity: dept.defaultPromptQuantity ?? f.promptQuantity,
                        isVariablePrice: dept.defaultIsVariablePrice ?? f.isVariablePrice,
                        crvFee: dept.defaultCrvFee ?? f.crvFee,
                      }));
                    }
                  }}
                  className={`h-9 w-full rounded-lg border px-3 text-sm ${departmentError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-input'}`}
                >
                  <option value="">Select department</option>
                  {departments.map((dept: any) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
                {departmentError && <p className="mt-1 text-xs text-red-500">{departmentError}</p>}

                {/* Product Categories */}
                {allCategories.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <label className="block text-sm font-medium text-foreground mb-2">Product Categories</label>
                    <MultiSelect
                      options={allCategories.map((cat: any) => ({ value: cat.id, label: cat.name }))}
                      selected={form.categoryIds}
                      onChange={(ids) => update('categoryIds', ids)}
                      placeholder="Select categories"
                      searchPlaceholder="Search categories..."
                      emptyMessage="No categories found"
                    />
                  </div>
                )}

                {/* Modifier Groups — prompted at the POS when this item is added */}
                {allModifierGroups.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <label className="block text-sm font-medium text-foreground mb-2">Modifier Groups</label>
                    <MultiSelect
                      options={allModifierGroups.map((g: any) => ({ value: g.id, label: g.name }))}
                      selected={form.modifierGroupIds}
                      onChange={(ids) => update('modifierGroupIds', ids)}
                      placeholder="Select modifier groups"
                      searchPlaceholder="Search modifier groups..."
                      emptyMessage="No modifier groups found"
                    />
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      The POS will prompt these options when this item is added to an order.
                    </p>
                  </div>
                )}
              </div>

              {/* Product Options */}
              <div className={scopeCardCls('tenant')} data-scope="tenant">
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <h3 className="text-base font-semibold">Product Options</h3>
                  <ScopeChip scope="tenant" />
                </div>
                <p className="mb-4 text-xs text-muted-foreground">Changing a switch changes it at every location.</p>
                <div className="space-y-3">
                  {[
                    { key: 'ebtEligible', label: 'EBT Eligible' },
                    { key: 'soldByWeight', label: 'Sold By Weight' },
                    { key: 'ageVerification', label: 'Age Verification' },
                    { key: 'excludeKitchenPrint', label: 'Kitchen Printing / KDS' },
                    { key: 'promptQuantity', label: 'Prompt Quantity at POS' },
                    { key: 'isVariablePrice', label: 'Variable Price' },
                  ].map(renderOptionToggle)}
                </div>
                <div className="mt-4 border-t border-border pt-4">
                  <label className="block text-sm font-medium text-foreground">Sort order (POS)</label>
                  <input
                    type="number"
                    value={form.sortOrder}
                    onChange={(e) => update('sortOrder', e.target.value)}
                    placeholder="lower = first; blank = alphabetical"
                    className={inputCls}
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Pin this product's position within its department/category on the POS.
                  </p>
                </div>
              </div>

              {/* Sales tax, coupon, CRV — per store */}
              <div className={scopeCardCls(storeAttrsScope)} data-scope={storeAttrsScope}>
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <h3 className="text-base font-semibold">Tax, Coupon &amp; Redemption Value</h3>
                  <ScopeChip scope={storeAttrsScope} locationName={selectedLocationName} />
                </div>
                {renderStoreAttrsScopeNote()}
                <div className="space-y-3">
                  {[
                    { key: 'taxable', label: 'Taxable' },
                    { key: 'couponEligible', label: 'Coupon Eligible' },
                  ].map(renderOptionToggle)}
                </div>
                {/* CRV Fee */}
                <div className="mt-4 pt-4 border-t border-border">
                  <label className="block text-sm font-medium text-foreground">Redemption Value Fee</label>
                  <p className="mb-2 mt-0.5 text-xs text-muted-foreground">
                    A deposit added to each bottle or can, like California&apos;s CRV or New York&apos;s bottle deposit.
                  </p>
                  {canManageCrvOptions && (
                    <CrvOptionsEditor open={crvEditorOpen} onClose={() => setCrvEditorOpen(false)} />
                  )}
                  {/* "Manage options" trails the amount chips so the fee the user
                    came here to pick reads first; the teal outline keeps it
                    clearly secondary to the filled selection chip. */}
                  <div className="flex flex-wrap items-center gap-2">
                    {crvOptions.map((cents) => (
                      <button
                        key={cents}
                        type="button"
                        onClick={() => update('crvFee', cents)}
                        className={`min-w-[3rem] rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${
                          form.crvFee === cents
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-white text-foreground border-border hover:bg-muted'
                        }`}
                      >
                        {formatCrvOption(cents)}
                      </button>
                    ))}
                    {canManageCrvOptions && (
                      <button
                        type="button"
                        onClick={() => setCrvEditorOpen(true)}
                        className="rounded-full border border-teal-600 bg-white px-3 py-1.5 text-xs font-medium text-teal-700 transition-colors hover:bg-teal-50"
                      >
                        Manage options
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Item Placement */}
              <div className={scopeCardCls(storeAttrsScope)} data-scope={storeAttrsScope}>
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <h3 className="text-base font-semibold">Product Placement</h3>
                  <ScopeChip scope={storeAttrsScope} locationName={selectedLocationName} />
                </div>
                {renderStoreAttrsScopeNote()}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-foreground">Building</label>
                    <input
                      type="text"
                      value={form.building}
                      onChange={(e) => update('building', e.target.value)}
                      placeholder="e.g. B1"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground">Aisle</label>
                    <input
                      type="text"
                      value={form.aisle}
                      onChange={(e) => update('aisle', e.target.value)}
                      placeholder="e.g. A3"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground">Bin / Bay</label>
                    <input
                      type="text"
                      value={form.bin}
                      onChange={(e) => update('bin', e.target.value)}
                      placeholder="e.g. B12"
                      className={inputCls}
                    />
                  </div>
                </div>

                <div className="mt-4 border-t border-border pt-4">
                  <p className="text-sm font-medium text-foreground">Expiry &amp; Batch</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Up to two batches on the shelf, each with its own expiry. Leave blank if the product doesn't expire.
                  </p>
                  {(
                    [
                      ['expiryDate', 'batchNo1', 1],
                      ['expiryDate2', 'batchNo2', 2],
                    ] as const
                  ).map(([dateKey, batchKey, n]) => (
                    <div key={n} className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-foreground">Batch No {n}</label>
                        <input
                          type="text"
                          maxLength={50}
                          value={form[batchKey]}
                          onChange={(e) => update(batchKey, e.target.value)}
                          placeholder={`e.g. BATCH${n}`}
                          className={inputCls}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground">Expiry Date {n}</label>
                        <DateInput
                          value={form[dateKey]}
                          onChange={(e) => update(dateKey, e.target.value)}
                          className={inputCls}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Custom Fields */}
              <div className={scopeCardCls('tenant')} data-scope="tenant">
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  <h3 className="text-base font-semibold">Custom Fields</h3>
                  <ScopeChip scope="tenant" />
                </div>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground">
                      Ingredients Notes <span className="text-xs text-muted-foreground">(optional)</span>
                    </label>
                    <input
                      type="text"
                      maxLength={500}
                      value={form.customField1}
                      onChange={(e) => update('customField1', e.target.value)}
                      className={inputCls}
                    />
                    <p className="mt-1 text-xs text-muted-foreground">
                      Printed as the ingredients notes on the product label from the label printer.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground">
                      Original / Non Club Price <span className="text-xs text-muted-foreground">(optional)</span>
                    </label>
                    <input
                      type="text"
                      maxLength={500}
                      value={form.customField2}
                      onChange={(e) => update('customField2', e.target.value)}
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground">
                      Custom Field <span className="text-xs text-muted-foreground">(optional)</span>
                    </label>
                    <input
                      type="text"
                      maxLength={500}
                      value={form.customField3}
                      onChange={(e) => update('customField3', e.target.value)}
                      className={inputCls}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : /* ═══════════════════ VENDORS TAB ═══════════════════ */
        activeTab === 'vendors' ? (
          <fieldset disabled={lockVendors} className={scopeCardCls('location')} data-scope="location">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <h3 className="flex flex-wrap items-center gap-2 text-base font-semibold">
                  Vendors
                  <ScopeChip scope="location" locationName={selectedLocationName} />
                </h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  Cheapest first. Switch the location in the header to manage another store&apos;s vendors.
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {canAddVendor && !lockVendors && (
                  <button
                    type="button"
                    onClick={() => setShowAddVendorModal(true)}
                    className="inline-flex h-9 items-center gap-1 rounded-lg px-3 text-sm font-medium text-primary hover:bg-primary/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <Plus className="h-3.5 w-3.5" aria-hidden="true" /> New vendor
                  </button>
                )}
                {!lockVendors && (
                  <button
                    type="button"
                    onClick={addVendor}
                    className="inline-flex h-9 items-center rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    Assign vendor
                  </button>
                )}
              </div>
            </div>

            {/* The fallback-list case decides WHERE the data lands, so it gets a
              banner rather than a clause at the end of a grey paragraph. */}
            {!selectedLocationId && (
              <div className="mb-4 flex gap-2 rounded-lg border border-info/30 bg-info/5 px-3 py-2.5 text-xs text-foreground">
                <Info className="mt-px h-4 w-4 shrink-0 text-info" aria-hidden="true" />
                <p>
                  No location selected — saving here updates the legacy/fallback vendor list, which applies to any
                  location without its own. Pick a location in the header to scope these to a specific store.
                </p>
              </div>
            )}

            {vendors.length === 0 ? (
              <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border py-10 text-center">
                <Store className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
                <p className="text-sm font-medium text-foreground">No vendors assigned</p>
                {!lockVendors && (
                  <>
                    <p className="max-w-sm text-xs text-muted-foreground">
                      Assign a vendor to track sourcing and enable auto-ordering.
                    </p>
                    <button
                      type="button"
                      onClick={addVendor}
                      className="mt-1 inline-flex h-9 items-center rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      Assign vendor
                    </button>
                  </>
                )}
              </div>
            ) : (
              <>
                <div className="table-scroll overflow-x-auto">
                  {/* Narrower than this the columns stop being readable, so the
                    table scrolls sideways rather than crushing every field. */}
                  <div className="min-w-[975px]">
                    <div className={cn(VENDOR_GRID, 'border-b border-border pb-1.5')}>
                      <span />
                      <span className={VENDOR_HEAD}>Vendor</span>
                      <span className={VENDOR_HEAD}>Unit</span>
                      <span className={cn(VENDOR_HEAD, 'text-right')}>Price</span>
                      <span className={VENDOR_HEAD}>Vendor SKU</span>
                      <span className={VENDOR_HEAD}>Vendor Product Name</span>
                      <span className={VENDOR_HEAD}>Effective</span>
                      <span />
                    </div>

                    {vendorDisplayOrder.map((origIdx, sortedPos) => {
                      const vendor = vendors[origIdx];
                      // Only a comparison of two or more says anything; a lone
                      // vendor is not "the best rate".
                      const isBest = sortedPos === 0 && !!vendor.vendorPrice && vendors.length > 1;
                      const isExpanded = expandedVendor === origIdx;
                      const vendorName = vendorNames.get(vendor.vendorId) || 'this vendor';

                      return (
                        <div key={origIdx}>
                          <div className={cn(VENDOR_GRID, 'border-b border-border/60 py-1.5 hover:bg-muted/40')}>
                            <button
                              type="button"
                              onClick={() => setExpandedVendor(isExpanded ? null : origIdx)}
                              aria-expanded={isExpanded}
                              aria-label={`${isExpanded ? 'Hide' : 'Show'} price details for ${vendorName}`}
                              className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                              <ChevronRight
                                className={cn('h-4 w-4 transition-transform', isExpanded && 'rotate-90')}
                                aria-hidden="true"
                              />
                            </button>

                            {/* The marker sits UNDER the select, not beside it:
                              SearchableSelect carries its own chevron, so sharing
                              the track clipped real vendor names to four
                              characters. Remove is how you unassign a vendor, so
                              the select's clear affordance is off here too. */}
                            <div className="flex min-w-0 flex-col justify-center">
                              <SearchableSelect
                                variant="cell"
                                className="min-w-0"
                                clearable={false}
                                // The table wrapper is an overflow container, which
                                // clips on BOTH axes — without this the menu was cut
                                // off 177px in and most vendors were unreachable.
                                portal
                                value={vendor.vendorId}
                                onChange={(next) => updateVendor(origIdx, 'vendorId', next)}
                                options={[...vendorNames].map(([value, label]) => ({ value, label }))}
                                placeholder="Select vendor"
                                searchPlaceholder="Search vendors..."
                                emptyMessage="No vendors found"
                              />
                              {isBest && (
                                // Dot + word + first position: never colour alone.
                                // green-700 rather than the success token, which is
                                // only 3.2:1 on white and fails AA at this size.
                                <span className="ml-2 inline-flex items-center gap-1 text-[11px] font-semibold text-green-700">
                                  <span className="h-1.5 w-1.5 rounded-full bg-success" aria-hidden="true" />
                                  Best rate
                                </span>
                              )}
                            </div>

                            <select
                              value={vendor.unitName}
                              onChange={(e) => updateVendor(origIdx, 'unitName', e.target.value)}
                              aria-label={`Vendor unit for ${vendorName}`}
                              className={vendorCellCls}
                            >
                              {unitOptionsFor(vendor.unitName).map((u) => (
                                <option key={u} value={u}>
                                  {u}
                                </option>
                              ))}
                            </select>

                            <div className="relative">
                              <span
                                aria-hidden="true"
                                className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground"
                              >
                                $
                              </span>
                              <input
                                type="text"
                                inputMode="decimal"
                                value={vendor.vendorPrice}
                                onChange={(e) => updateVendor(origIdx, 'vendorPrice', sanitizeMoney(e.target.value))}
                                // Re-rank on commit, not per keystroke.
                                onBlur={commitVendorOrder}
                                placeholder="0.00"
                                aria-label={`Vendor price for ${vendorName}`}
                                className={cn(vendorCellCls, 'pl-5 text-right tabular-nums')}
                              />
                            </div>

                            <input
                              type="text"
                              value={vendor.vendorItemCode}
                              onChange={(e) => updateVendor(origIdx, 'vendorItemCode', e.target.value)}
                              placeholder="—"
                              aria-label={`Vendor SKU for ${vendorName}`}
                              className={cn(vendorCellCls, 'tabular-nums')}
                            />

                            <input
                              type="text"
                              value={vendor.vendorProductName}
                              onChange={(e) => updateVendor(origIdx, 'vendorProductName', e.target.value)}
                              placeholder="—"
                              aria-label={`Vendor product name for ${vendorName}`}
                              className={vendorCellCls}
                            />

                            {/* Read-only here; edited in the row's own panel below,
                              so one value never has two live controls. */}
                            <span className="truncate px-2 text-sm tabular-nums text-muted-foreground">
                              {vendor.effectiveDate || '—'}
                            </span>

                            {!lockVendors && (
                              <button
                                type="button"
                                onClick={() => setVendorPendingRemoval(origIdx)}
                                aria-label={`Remove ${vendorName}`}
                                className="flex h-11 w-11 items-center justify-center rounded-md text-muted-foreground hover:bg-red-50 hover:text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              >
                                <Trash2 className="h-4 w-4" aria-hidden="true" />
                              </button>
                            )}
                          </div>

                          {isExpanded && (
                            <div className="border-b border-border/60 bg-muted/20 px-3 py-3 sm:pl-[14rem]">
                              <div className="grid max-w-lg grid-cols-1 gap-3 sm:grid-cols-2">
                                <div>
                                  <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                                    Reason for this price
                                  </label>
                                  <select
                                    value={vendor.priceReason}
                                    onChange={(e) => updateVendor(origIdx, 'priceReason', e.target.value)}
                                    className={cn(smallInputCls, 'mt-1')}
                                  >
                                    <option value="">Select reason</option>
                                    <option value="Promotion">Promotion</option>
                                    <option value="Price Increase">Price Increase</option>
                                    <option value="Price Decrease">Price Decrease</option>
                                    <option value="New Contract">New Contract</option>
                                    <option value="Miscellaneous">Miscellaneous</option>
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                                    Effective from
                                  </label>
                                  <DateInput
                                    value={vendor.effectiveDate}
                                    onChange={(e) => updateVendor(origIdx, 'effectiveDate', e.target.value)}
                                    className={cn(smallInputCls, 'mt-1')}
                                    portal
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <p className="mt-3 text-xs text-muted-foreground">
                  Re-ranks when you leave a price field, not while you type — so a row never moves under the cursor.
                </p>
              </>
            )}

            <ConfirmDialog
              open={vendorPendingRemoval !== null}
              title="Remove vendor?"
              message={
                vendorPendingRemoval !== null
                  ? `${
                      vendorNames.get(vendors[vendorPendingRemoval]?.vendorId) || 'This vendor'
                    } will be unlinked from this product when you save. Its price history is kept.`
                  : ''
              }
              confirmLabel="Remove"
              onConfirm={() => {
                if (vendorPendingRemoval !== null) removeVendor(vendorPendingRemoval);
                setVendorPendingRemoval(null);
              }}
              onCancel={() => setVendorPendingRemoval(null)}
            />

            {/* Vendor Price History */}
            {initialData?.id && <VendorPriceHistoryPanel productId={initialData.id} />}
          </fieldset>
        ) : activeTab === 'price-history' ? (
          <PriceHistoryTab productId={initialData?.id} product={initialData} />
        ) : activeTab === 'audit-log' ? (
          <AuditLogTab productId={initialData?.id} product={initialData} />
        ) : activeTab === 'intelligence' && canSeeIntelligence && initialData?.id ? (
          <IntelligenceTab product={initialData} />
        ) : (
          <div className="flex h-64 items-center justify-center rounded-lg border border-border bg-white text-muted-foreground">
            This tab is coming soon.
          </div>
        )}
      </fieldset>

      {/* Actions — only show on Product & Pricing tab.
          Pinned to the bottom of the scroll area (AppShell's <main> is the
          scroll container) so Create/Update stays reachable on this very long
          form instead of forcing a scroll to the end every time.

          The negative insets cancel <main>'s p-4 / lg:p-6 padding: -mx spans
          the bar full width, and the negative `bottom` is what keeps it flush
          with the bottom edge. Sticky measures its inset from the scroll
          container's content box, so a plain `bottom-0` parks the bar one
          padding-height above the viewport floor and it visibly hovers. A
          negative bottom *margin* does not fix that — sticky aligns the margin
          edge, so the two cancel out. Verified: 24px gap with bottom-0 at
          lg:p-6, 0 with -bottom-6. It still settles inline at the end of the
          form, which is why the value must track the padding, not exceed it. */}
      {/* The editable tabs all submit through this one form, so the save bar has
          to reach all of them. It used to render only on Product & Pricing, which
          left the Vendors tab with no way to save what you had just typed —
          `payees` comes off the same handleSubmit. Price History and Audit Log
          are read-only, so they still get none. */}
      {canSaveProduct &&
        (activeTab === 'product-pricing' || activeTab === 'vendors' || activeTab === 'online-store') && (
          <div className="sticky -bottom-4 z-20 -mx-4 flex items-center justify-end gap-3 border-t border-border bg-white px-4 py-3 shadow-[0_-2px_8px_rgba(15,23,42,0.06)] lg:-bottom-6 lg:-mx-6 lg:px-6">
            <a
              href="#/pos-management"
              className="inline-flex h-9 items-center rounded-lg border border-border px-4 text-sm font-medium text-foreground hover:bg-muted"
            >
              Cancel
            </a>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex h-9 items-center rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : initialData ? 'Update' : 'Create'}
            </button>
          </div>
        )}
      {showAddVendorModal &&
        createPortal(<AddVendorModal onClose={() => setShowAddVendorModal(false)} />, document.body)}
    </form>
  );
}

function VendorPriceHistoryPanel({ productId }: { productId: string }) {
  const [page, setPage] = useState(1);
  const [vendorFilter, setVendorFilter] = useState('');
  const { data, isLoading } = useVendorPriceHistory(productId, {
    page,
    perPage: 10,
    ...(vendorFilter ? { payeeId: vendorFilter } : {}),
  });
  const { data: usersData } = useUsers({ perPage: 100 });
  const userMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of usersData?.data ?? []) {
      map.set(u.id, u.name || u.email);
    }
    return map;
  }, [usersData]);

  // Get all entries (unfiltered) to build vendor list for dropdown
  const { data: allData } = useVendorPriceHistory(productId, { perPage: 1000 });
  const vendorNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of allData?.data ?? []) map.set(e.payeeId, e.payeeName);
    return Array.from(map.entries());
  }, [allData]);

  const entries = data?.data ?? [];
  const pagination = data?.pagination;

  if (isLoading && entries.length === 0) return null;
  if (!isLoading && entries.length === 0 && !vendorFilter) return null;

  return (
    <div className="mt-4 rounded-lg border border-border bg-white p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <History className="h-4 w-4 text-primary" />
          <h4 className="text-sm font-semibold">Vendor Price History</h4>
        </div>
        {vendorNames.length > 1 && (
          <select
            value={vendorFilter}
            onChange={(e) => {
              setVendorFilter(e.target.value);
              setPage(1);
            }}
            className="h-8 rounded-lg border border-input bg-background px-2 text-xs"
          >
            <option value="">All Vendors</option>
            {vendorNames.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[600px] text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">Vendor</th>
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">Old Price</th>
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">New Price</th>
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">Reason</th>
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">Effective Date</th>
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">Changed By</th>
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">Changed At</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry: any) => (
              <tr key={entry.id} className="border-b border-border last:border-0">
                <td className="px-3 py-2">{entry.payeeName}</td>
                <td className="px-3 py-2 font-mono text-muted-foreground">
                  {entry.oldPrice != null ? `$${parseFloat(entry.oldPrice).toFixed(2)}` : '\u2014'}
                </td>
                <td className="px-3 py-2 font-mono">
                  {entry.newPrice != null ? `$${parseFloat(entry.newPrice).toFixed(2)}` : '\u2014'}
                </td>
                <td className="px-3 py-2">
                  {entry.reason ? (
                    <span className="inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                      {entry.reason}
                    </span>
                  ) : (
                    '\u2014'
                  )}
                </td>
                <td className="px-3 py-2 text-muted-foreground">
                  {entry.effectiveDate ? new Date(entry.effectiveDate).toLocaleDateString() : '\u2014'}
                </td>
                <td className="px-3 py-2 text-muted-foreground">
                  {userMap.get(entry.changedBy) || entry.changedBy?.slice(0, 8) + '...'}
                </td>
                <td className="px-3 py-2 text-muted-foreground">
                  {new Date(entry.changedAt).toLocaleDateString()}{' '}
                  <span className="text-xs">{new Date(entry.changedAt).toLocaleTimeString()}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground mt-3">
          <span>{pagination.total} total entries</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="rounded-lg border border-border px-3 py-1 text-sm hover:bg-muted disabled:opacity-50"
            >
              Previous
            </button>
            <span className="px-2 py-1">
              Page {page} of {pagination.totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= pagination.totalPages}
              className="rounded-lg border border-border px-3 py-1 text-sm hover:bg-muted disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PriceHistoryTab({ productId, product }: { productId: string; product: any }) {
  const [priceTypeFilter, setPriceTypeFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const PER_PAGE = 25;
  // Fetch everything once; the products service caps at 1000. We filter +
  // paginate client-side so the active location filter from the global picker
  // and the priceType filter both compose without re-fetching.
  const { data: allHistoryData, isLoading } = useProductPriceHistory(productId, { perPage: 1000 });
  const { data: usersData } = useUsers({ perPage: 100 });
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const { data: locationsData } = useLocations();
  const locationNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const l of locationsData?.data ?? []) {
      map.set(l.id, l.nickname || l.name);
    }
    return map;
  }, [locationsData]);
  const userMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of usersData?.data ?? []) {
      map.set(u.id, u.name || u.email);
    }
    return map;
  }, [usersData]);

  const allHistory = allHistoryData?.data ?? [];

  const locationTypeMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const l of locationsData?.data ?? []) map.set(l.id, l.type);
    return map;
  }, [locationsData]);
  const selectedIsWarehouse = selectedLocationId ? isWarehouseType(locationTypeMap.get(selectedLocationId)) : false;

  // Per-location entries are tied to a specific location; tenant-wide rows
  // split by row type:
  //   - scalar `cost`/`sellingPrice` (no `unitName`) are store retail prices,
  //     so we hide them in warehouse context.
  //   - unit-bearing rows (case/pack) are warehouse case prices, so we hide
  //     them in store context.
  // Both kinds show when no location is selected (super-admin / all view).
  const locationFiltered = useMemo(() => {
    return allHistory.filter((e: any) => {
      if (e.locationId != null) {
        return !selectedLocationId || e.locationId === selectedLocationId;
      }
      if (!selectedLocationId) return true;
      return selectedIsWarehouse ? !!e.unitName : !e.unitName;
    });
  }, [allHistory, selectedLocationId, selectedIsWarehouse]);

  const typeFiltered = useMemo(() => {
    if (!priceTypeFilter) return locationFiltered;
    return locationFiltered.filter((e: any) => e.priceType === priceTypeFilter);
  }, [locationFiltered, priceTypeFilter]);

  const total = typeFiltered.length;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  // Clamp the page so a narrowing filter (location / price type) lands the
  // user on a valid page instead of an empty one.
  const safePage = Math.min(Math.max(1, page), totalPages);
  const history = typeFiltered.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  // Compute original set date (product creation) and last modified date per
  // price type — scoped to the active location so the counts match the table.
  const priceSummary = useMemo(() => {
    const costChanges = locationFiltered.filter((e: any) => e.priceType === 'cost');
    const sellingChanges = locationFiltered.filter((e: any) => e.priceType === 'selling');
    return {
      costLastModified: costChanges.length > 0 ? costChanges[0] : null,
      sellingLastModified: sellingChanges.length > 0 ? sellingChanges[0] : null,
      costChangeCount: costChanges.length,
      sellingChangeCount: sellingChanges.length,
    };
  }, [locationFiltered]);

  const createdAt = formatDateTime(product?.createdAt);

  // Resolve the price the user actually sees for the active location, in the
  // same precedence the form/POS use:
  //   1. per-location override (productPrices)
  //   2. warehouse case row (productUnits matching the location's selling
  //      unit) — case prices live here, not on the scalar product field
  //   3. scalar product.cost / product.sellingPrice fallback
  // Without this, the warehouse view shows the store retail value instead of
  // the case price.
  const currentPriceFor = (field: 'cost' | 'sellingPrice'): string | number | null | undefined => {
    if (!selectedLocationId) return product?.[field];
    const override = (product?.prices ?? []).find((p: any) => p.locationId === selectedLocationId);
    if (override && override[field] != null && override[field] !== '') return override[field];
    if (selectedIsWarehouse) {
      const sellingUnit = product?.defaultSellingUnit;
      if (sellingUnit) {
        const caseRow = (product?.units ?? []).find(
          (u: any) => u?.unitName && String(u.unitName).toLowerCase() === String(sellingUnit).toLowerCase(),
        );
        if (caseRow && caseRow[field] != null && caseRow[field] !== '') return caseRow[field];
      }
    }
    return product?.[field];
  };
  const currentCost = currentPriceFor('cost');
  const currentSelling = currentPriceFor('sellingPrice');
  const priceContextLabel = selectedLocationName
    ? selectedIsWarehouse
      ? `${selectedLocationName} · per ${product?.defaultSellingUnit ?? 'case'}`
      : `${selectedLocationName}`
    : 'tenant-wide';

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="rounded-lg border border-border bg-white p-4">
          <div className="flex items-center justify-between">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Cost Price</div>
            <div className="text-[10px] font-medium text-muted-foreground">{priceContextLabel}</div>
          </div>
          <div className="mt-2 space-y-1.5">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Original set date</span>
              <span className="font-medium">{createdAt}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Last modified</span>
              <span className="font-medium">{formatDateTime(priceSummary.costLastModified?.changedAt)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Current value</span>
              <span className="font-semibold">{formatCurrency(currentCost)}</span>
            </div>
            <div className="text-xs text-muted-foreground">
              {priceSummary.costChangeCount} change{priceSummary.costChangeCount !== 1 ? 's' : ''} recorded
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-border bg-white p-4">
          <div className="flex items-center justify-between">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Selling / Club Price
            </div>
            <div className="text-[10px] font-medium text-muted-foreground">{priceContextLabel}</div>
          </div>
          <div className="mt-2 space-y-1.5">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Original set date</span>
              <span className="font-medium">{createdAt}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Last modified</span>
              <span className="font-medium">{formatDateTime(priceSummary.sellingLastModified?.changedAt)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Current value</span>
              <span className="font-semibold">{formatCurrency(currentSelling)}</span>
            </div>
            <div className="text-xs text-muted-foreground">
              {priceSummary.sellingChangeCount} change{priceSummary.sellingChangeCount !== 1 ? 's' : ''} recorded
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <History className="h-5 w-5 text-primary" />
          <h3 className="text-base font-semibold">Change Log</h3>
          {selectedLocationName && <span className="text-xs text-muted-foreground">at {selectedLocationName}</span>}
        </div>
        <select
          value={priceTypeFilter}
          onChange={(e) => {
            setPriceTypeFilter(e.target.value);
            setPage(1);
          }}
          className="h-9 rounded-lg border border-input bg-background px-3 text-sm"
        >
          <option value="">All Changes</option>
          <option value="cost">Cost Price Only</option>
          <option value="selling">Selling / Club Price Only</option>
        </select>
      </div>

      <div className="rounded-lg border border-border bg-white">
        {isLoading ? (
          <div className="flex h-32 items-center justify-center text-muted-foreground">Loading...</div>
        ) : history.length === 0 ? (
          <div className="flex h-32 items-center justify-center text-muted-foreground">
            No price changes recorded yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Created At</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Modified At</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Type</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Unit</th>
                  <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Old Price</th>
                  <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">New Price</th>
                  <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">Change</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Changed By</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Reason</th>
                </tr>
              </thead>
              <tbody>
                {history.map((entry: any) => {
                  const hasOldValue = entry.oldValue != null && entry.oldValue !== '';
                  const oldVal = hasOldValue ? parseFloat(entry.oldValue) : 0;
                  const newVal = parseFloat(entry.newValue) || 0;
                  const diff = newVal - oldVal;
                  const isIncrease = diff > 0;
                  const isCost = entry.priceType === 'cost';
                  // Cost ↓ = Good (Green), Cost ↑ = Bad (Red)
                  // Selling ↑ = Good (Green), Selling ↓ = Bad (Red)
                  // N/A old value = Green (first-time entry), Equal = Blue (neutral)
                  const changeColor = !hasOldValue
                    ? 'text-green-600'
                    : diff === 0
                      ? 'text-blue-600'
                      : isCost
                        ? isIncrease
                          ? 'text-red-600'
                          : 'text-green-600'
                        : isIncrease
                          ? 'text-green-600'
                          : 'text-red-600';
                  return (
                    <tr key={entry.id} className="border-b border-border last:border-0">
                      <td className="px-4 py-2.5 text-muted-foreground">{createdAt}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">
                        {new Date(entry.changedAt).toLocaleDateString()}{' '}
                        <span className="text-xs">{new Date(entry.changedAt).toLocaleTimeString()}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                            entry.priceType === 'cost'
                              ? 'bg-purple-50 text-purple-700 border border-purple-200'
                              : 'bg-blue-50 text-blue-700 border border-blue-200'
                          }`}
                        >
                          {entry.priceType === 'cost' ? 'Cost' : 'Selling'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-xs">
                        {entry.unitName ? (
                          <span className="inline-flex rounded-full bg-muted px-2 py-0.5 font-medium text-foreground">
                            {entry.unitName}
                            {entry.locationId && !selectedLocationId && (
                              <span className="ml-1 text-muted-foreground">
                                · {locationNames.get(entry.locationId) ?? 'location'}
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">product</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono">{formatCurrency(entry.oldValue)}</td>
                      <td className="px-4 py-2.5 text-right font-mono">{formatCurrency(entry.newValue)}</td>
                      <td className="px-4 py-2.5 text-right">
                        <span className={`font-medium ${changeColor}`}>
                          {isIncrease ? '+' : ''}
                          {formatCurrency(Math.abs(diff).toFixed(2))}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-muted-foreground">{auditActorLabel(entry, userMap)}</td>
                      <td className="px-4 py-2.5 text-muted-foreground text-xs">{entry.reason || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>{total} total changes</span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(Math.max(1, safePage - 1))}
              disabled={safePage <= 1}
              className="rounded-lg border border-border px-3 py-1 text-sm hover:bg-muted disabled:opacity-50"
            >
              Previous
            </button>
            <span className="px-2 py-1">
              Page {safePage} of {totalPages}
            </span>
            <button
              onClick={() => setPage(Math.min(totalPages, safePage + 1))}
              disabled={safePage >= totalPages}
              className="rounded-lg border border-border px-3 py-1 text-sm hover:bg-muted disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const FIELD_LABELS: Record<string, string> = {
  name: 'Product Name',
  displayName: 'Display Name',
  sku: 'SKU',
  primaryBarcode: 'Primary Barcode',
  additionalBarcodes: 'Additional Barcodes',
  building: 'Building',
  aisle: 'Aisle',
  bin: 'Bin/Bay',
  departmentId: 'Department',
  sellingPrice: 'Selling / Club Price',
  cost: 'Cost Price',
  inStockQty: 'In Stock Qty',
  reorderThreshold: 'Reorder Threshold',
  soldByWeight: 'Sold By Weight',
  ageVerification: 'Age Verification',
  ebtEligible: 'EBT Eligible',
  couponEligible: 'Coupon Eligible',
  excludeKitchenPrint: 'Kitchen Print',
  taxable: 'Taxable',
  taxRate: 'Tax Rate',
  defaultSellingUnit: 'Selling Unit',
  defaultPurchasingUnit: 'Purchasing Unit',
  isVariablePrice: 'Variable Price',
  promptQuantity: 'Prompt Quantity',
  crvFee: 'Redemption Value Fee',
  rewardPoints: 'Reward Points',
  pricePerLb: 'Price Per Lb',
  categories: 'Categories',
  modifierGroups: 'Modifier Groups',
  vendors: 'Vendors',
  units: 'Case/Pack Units',
  locations: 'Location Assignments',
  priceOverrides: 'Location Price Overrides',
  customField1: 'Ingredients Notes',
  customField2: 'Original / Non Club Price',
  customField3: 'Custom Field',
  sortOrder: 'Sort Order (POS)',
  expiryDate: 'Expiry Date',
  expiryDate1: 'Expiry Date 1',
  batchNo1: 'Batch No 1',
  expiryDate2: 'Expiry Date 2',
  batchNo2: 'Batch No 2',
  forceIncludeAutoPo: 'Add to PO',
};

/** Surfaces an audited change can come from (product_audit_log.source). */
const AUDIT_SOURCE_LABELS: Record<string, string> = {
  mobile: 'Mobile',
  dashboard: 'Dashboard',
  pos: 'POS',
};

const AUDIT_ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super Admin',
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  employee: 'Employee',
};

/** Where in the mobile app a change was made (product_audit_log.section). */
const AUDIT_SECTION_LABELS: Record<string, string> = {
  basicInfo: 'Basic Info',
  unitsOfMeasure: 'Units of Measure',
  casePackPallet: 'Case / Pack / Pallet',
  storeInventory: 'Store Inventory',
  department: 'Department',
  productOptions: 'Product Options',
  productPlacement: 'Product Placement',
  customFields: 'Custom Fields',
  priceStock: 'Price & Stock',
  purchaseOrder: 'Add to PO',
  vendors: 'Vendors',
};

/**
 * "Changed By" for an audit or price-history row: `Mobile - John - Manager`
 * when the row records its source (every change since the audit log gained
 * attribution), else the user's name as before. The name stored with the
 * row wins over today's user list, so a renamed or removed user still reads
 * as they were when they made the change.
 */
function auditActorLabel(
  entry: { source?: string | null; actorName?: string | null; actorRole?: string | null; changedBy?: string },
  userMap: Map<string, string>,
): string {
  const name = entry.actorName || (entry.changedBy && userMap.get(entry.changedBy)) || null;
  const fallback = name ?? (entry.changedBy ? `${entry.changedBy.slice(0, 8)}...` : '—');
  if (!entry.source) return fallback;
  const source = AUDIT_SOURCE_LABELS[entry.source] ?? entry.source;
  const role = entry.actorRole ? (AUDIT_ROLE_LABELS[entry.actorRole] ?? entry.actorRole) : null;
  return [source, fallback, role].filter(Boolean).join(' - ');
}

const JSON_FIELDS = [
  'categories',
  'modifierGroups',
  'vendors',
  'units',
  'locations',
  'priceOverrides',
  'additionalBarcodes',
];

function getFieldLabel(fieldName: string, locationNames?: Map<string, string>): string {
  if (fieldName.startsWith('vendorPrice:')) {
    return `Vendor Price: ${fieldName.slice('vendorPrice:'.length)}`;
  }
  if (fieldName.startsWith('units:')) {
    const locId = fieldName.slice('units:'.length);
    const locName = locationNames?.get(locId);
    return locName ? `Case/Pack Units · ${locName}` : `Case/Pack Units (${locId.slice(0, 8)}…)`;
  }
  return FIELD_LABELS[fieldName] || fieldName;
}

type UnitRow = {
  unitName?: string | null;
  baseQty?: string | number | null;
  sellingPrice?: string | number | null;
  cost?: string | number | null;
  upc?: string | null;
};

function fmtAuditMoney(v: string | number | null | undefined): string {
  if (v == null || v === '') return '—';
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : String(v);
}

function fmtAuditQty(v: string | number | null | undefined): string {
  if (v == null || v === '') return '—';
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (!Number.isFinite(n)) return String(v);
  return String(parseFloat(n.toFixed(3)));
}

function UnitsAuditValue({ rows }: { rows: UnitRow[] }) {
  if (!rows.length) return <span className="text-muted-foreground italic">no units</span>;
  return (
    <div className="space-y-0.5 font-sans">
      {rows.map((u, i) => (
        <div key={i} className="text-xs">
          <span className="font-medium">{u.unitName || '—'}</span>
          <span className="text-muted-foreground"> · qty {fmtAuditQty(u.baseQty)}</span>
          <span> · {fmtAuditMoney(u.sellingPrice)}</span>
          {u.cost != null && u.cost !== '' && (
            <span className="text-muted-foreground"> · cost {fmtAuditMoney(u.cost)}</span>
          )}
          {u.upc && <span className="text-muted-foreground"> · upc {u.upc}</span>}
        </div>
      ))}
    </div>
  );
}

// Name lookups so the audit log shows what actually changed (e.g. which
// department / categories / modifier groups) instead of opaque UUIDs.
interface AuditNameMaps {
  departments: Map<string, string>;
  categories: Map<string, string>;
  modifierGroups: Map<string, string>;
}

// Compact fallback for an id we can't resolve to a name (e.g. the entity was
// deleted after the change was recorded): "76e6e137…" rather than a full UUID.
function shortId(id: string): string {
  return /^[0-9a-f]{8}-/i.test(id) ? `${id.slice(0, 8)}…` : id;
}

// Resolve a JSON array of ids to a comma-separated list of names.
function resolveIdList(value: string, map: Map<string, string>): string | null {
  try {
    const arr = JSON.parse(value);
    if (!Array.isArray(arr)) return null;
    if (arr.length === 0) return 'none';
    return arr.map((id: string) => map.get(id) || shortId(id)).join(', ');
  } catch {
    return null;
  }
}

function formatAuditValue(fieldName: string, value: string | null, names?: AuditNameMaps): ReactNode {
  if (value == null || value === '') return '—';
  if (fieldName === 'units' || fieldName.startsWith('units:')) {
    try {
      const arr = JSON.parse(value);
      if (Array.isArray(arr)) return <UnitsAuditValue rows={arr as UnitRow[]} />;
    } catch {
      /* not JSON, fall through to raw */
    }
  }
  // Resolve id-bearing fields to human-readable names.
  if (fieldName === 'departmentId') {
    return names?.departments.get(value) ?? shortId(value);
  }
  if (fieldName === 'categories' && names) {
    const resolved = resolveIdList(value, names.categories);
    if (resolved !== null) return resolved;
  }
  if (fieldName === 'modifierGroups' && names) {
    const resolved = resolveIdList(value, names.modifierGroups);
    if (resolved !== null) return resolved;
  }
  if (JSON_FIELDS.includes(fieldName)) {
    try {
      const arr = JSON.parse(value);
      if (Array.isArray(arr)) return `${arr.length} item${arr.length !== 1 ? 's' : ''}`;
    } catch {
      /* not JSON, show raw */
    }
  }
  if (
    fieldName === 'sellingPrice' ||
    fieldName === 'cost' ||
    fieldName === 'pricePerLb' ||
    fieldName === 'taxRate' ||
    fieldName.startsWith('vendorPrice:')
  ) {
    return `$${parseFloat(value).toFixed(2)}`;
  }
  if (fieldName === 'crvFee') {
    const cents = parseInt(value);
    return cents === 0 ? 'None' : `${cents}\u00a2`;
  }
  if (value === 'true') return 'Yes';
  if (value === 'false') return 'No';
  return value;
}

function AuditLogTab({ productId, product }: { productId: string; product: any }) {
  const [fieldFilter, setFieldFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const PER_PAGE = 25;
  // Fetch everything once and filter/paginate client-side so the active
  // location filter and the field filter compose without re-fetching.
  const { data: allData, isLoading } = useProductAuditLog(productId, { perPage: 1000 });
  const { data: usersData } = useUsers({ perPage: 100 });
  const { selectedLocationId, selectedLocationName } = useLocationStore();
  const userMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of usersData?.data ?? []) {
      map.set(u.id, u.name || u.email);
    }
    return map;
  }, [usersData]);
  const { data: locationsData } = useLocations();
  const locationNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const l of locationsData?.data ?? []) {
      map.set(l.id, l.nickname || l.name);
    }
    return map;
  }, [locationsData]);

  // Resolve id-bearing audit fields (department / categories / modifier groups)
  // to names so a tenant can see what actually changed, not raw UUIDs.
  const { data: deptData } = useDepartments();
  const { data: catData } = useCategories();
  const { data: modGroupData } = useModifierGroups();
  const nameMaps = useMemo<AuditNameMaps>(
    () => ({
      departments: new Map((deptData?.data ?? []).map((d: any) => [d.id, d.name])),
      categories: new Map((catData?.data ?? []).map((c: any) => [c.id, c.name])),
      modifierGroups: new Map((modGroupData?.data ?? []).map((g: any) => [g.id, g.name])),
    }),
    [deptData, catData, modGroupData],
  );

  const allEntries = allData?.data ?? [];

  const locationTypeMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const l of locationsData?.data ?? []) map.set(l.id, l.type);
    return map;
  }, [locationsData]);
  const selectedIsWarehouse = selectedLocationId ? isWarehouseType(locationTypeMap.get(selectedLocationId)) : false;

  // Filter rules in location context:
  //   - `units:<locId>` ↔ per-location unit/case change. Match selected
  //     location only.
  //   - `units` ↔ tenant-wide case/pack price. Warehouse-only concept, hide
  //     in store context.
  //   - `cost` / `sellingPrice` ↔ scalar store retail. Hide in warehouse
  //     context (warehouse case prices live in `units`/`units:<locId>`).
  //   - everything else (name, sku, ...): tenant-wide universal fields,
  //     always pass.
  const locationFiltered = useMemo(() => {
    if (!selectedLocationId) return allEntries;
    return allEntries.filter((e: any) => {
      const fn = String(e.fieldName ?? '');
      // A row recorded against one store (a per-store price, stock or unit
      // change) shows only at that store.
      if (e.locationId) return e.locationId === selectedLocationId;
      if (fn.startsWith('units:')) return fn.slice('units:'.length) === selectedLocationId;
      if (fn === 'units') return selectedIsWarehouse;
      if (fn === 'cost' || fn === 'sellingPrice') return !selectedIsWarehouse;
      return true;
    });
  }, [allEntries, selectedLocationId, selectedIsWarehouse]);

  const fieldFiltered = useMemo(() => {
    if (!fieldFilter) return locationFiltered;
    return locationFiltered.filter((e: any) => e.fieldName === fieldFilter);
  }, [locationFiltered, fieldFilter]);

  const total = fieldFiltered.length;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const entries = fieldFiltered.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  // Field-name dropdown source: only fields visible at the active location.
  const fieldNames = useMemo(() => {
    const names = new Set<string>();
    for (const e of locationFiltered) names.add(e.fieldName);
    return Array.from(names).sort();
  }, [locationFiltered]);

  const createdAt = formatDateTime(product?.createdAt);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ClipboardList className="h-5 w-5 text-primary" />
          <h3 className="text-base font-semibold">Audit Log</h3>
          <span className="text-xs text-muted-foreground">
            {selectedLocationName ? `Changes at ${selectedLocationName}` : 'All field changes tracked'}
          </span>
        </div>
        <select
          value={fieldFilter}
          onChange={(e) => {
            setFieldFilter(e.target.value);
            setPage(1);
          }}
          className="h-9 rounded-lg border border-input bg-background px-3 text-sm"
        >
          <option value="">All Fields</option>
          {fieldNames.map((f) => (
            <option key={f} value={f}>
              {getFieldLabel(f, locationNames)}
            </option>
          ))}
        </select>
      </div>

      <div className="rounded-lg border border-border bg-white">
        {isLoading ? (
          <div className="flex h-32 items-center justify-center text-muted-foreground">Loading...</div>
        ) : entries.length === 0 ? (
          <div className="flex h-32 items-center justify-center text-muted-foreground">No changes recorded yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px] text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Created At</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Modified At</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Field</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Old Value</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">New Value</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Section</th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Changed By</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry: any) => (
                  <tr key={entry.id} className="border-b border-border last:border-0">
                    <td className="px-4 py-2.5 text-muted-foreground">{createdAt}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">
                      {new Date(entry.changedAt).toLocaleDateString()}{' '}
                      <span className="text-xs">{new Date(entry.changedAt).toLocaleTimeString()}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="inline-flex rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground">
                        {getFieldLabel(entry.fieldName, locationNames)}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 align-top font-mono text-muted-foreground">
                      {formatAuditValue(entry.fieldName, entry.oldValue, nameMaps)}
                    </td>
                    <td className="px-4 py-2.5 align-top font-mono">
                      {formatAuditValue(entry.fieldName, entry.newValue, nameMaps)}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">
                      {entry.section ? (AUDIT_SECTION_LABELS[entry.section] ?? entry.section) : '—'}
                      {entry.locationId && !selectedLocationId && (
                        <div>{locationNames.get(entry.locationId) ?? 'store'}</div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground">{auditActorLabel(entry, userMap)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>{total} total changes</span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(Math.max(1, safePage - 1))}
              disabled={safePage <= 1}
              className="rounded-lg border border-border px-3 py-1 text-sm hover:bg-muted disabled:opacity-50"
            >
              Previous
            </button>
            <span className="px-2 py-1">
              Page {safePage} of {totalPages}
            </span>
            <button
              onClick={() => setPage(Math.min(totalPages, safePage + 1))}
              disabled={safePage >= totalPages}
              className="rounded-lg border border-border px-3 py-1 text-sm hover:bg-muted disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Add Vendor Modal (reuses SP payee creation) ──

const vendorInputBase = 'h-9 w-full rounded-lg border bg-white px-3 text-sm outline-none focus:ring-1';
const vendorInputNormal = `${vendorInputBase} border-input focus:border-primary focus:ring-primary`;
const vendorInputError = `${vendorInputBase} border-red-500 focus:border-red-500 focus:ring-red-500`;

function VendorFieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p className="mt-1 text-xs text-red-500" role="alert">
      {message}
    </p>
  );
}

function vendorFieldCls(fieldErrors: Record<string, string>, field: string): string {
  return fieldErrors[field] ? vendorInputError : vendorInputNormal;
}

function AddVendorModal({ onClose }: { onClose: () => void }) {
  const createVendor = useCreateSpPayee();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
  });

  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [duplicate, setDuplicate] = useState<{
    id: string;
    name: string;
    email: string;
    company: string | null;
  } | null>(null);

  const update = (field: string, value: string) => setFormData((prev) => ({ ...prev, [field]: value }));
  const markTouched = (field: string) => setTouched((prev) => ({ ...prev, [field]: true }));

  const handleAlphaInput = (field: string, value: string) => {
    const filtered = value.replace(/[^a-zA-Z\s]/g, '');
    update(field, filtered);
  };

  const fieldErrors = useMemo<Record<string, string>>(() => {
    const errors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.firstName)) {
      errors.firstName = 'Only alphabets and spaces are allowed';
    } else if (formData.firstName.trim().length < 2) {
      errors.firstName = 'First name must be at least 2 characters';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.lastName)) {
      errors.lastName = 'Only alphabets and spaces are allowed';
    } else if (formData.lastName.trim().length < 1) {
      errors.lastName = 'Last name must be at least 1 character';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email address';
    }

    {
      const phoneErr = validatePhone(formData.phone);
      if (phoneErr) errors.phone = phoneErr;
    }

    if (!formData.company.trim()) {
      errors.company = 'Company name is required';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.company)) {
      errors.company = 'Only alphabets and spaces are allowed';
    } else if (formData.company.trim().length < 2) {
      errors.company = 'Company name must be at least 2 characters';
    }

    return errors;
  }, [formData]);

  const isFormValid = Object.keys(fieldErrors).length === 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const allTouched: Record<string, boolean> = {};
    Object.keys(formData).forEach((key) => {
      allTouched[key] = true;
    });
    setTouched(allTouched);

    if (!isFormValid) return;

    setDuplicate(null);
    try {
      await createVendor.mutateAsync({
        name: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
        email: formData.email.trim() || null,
        phone: formData.phone || null,
        company: formData.company.trim() || null,
      } as any);
      toast.success('Vendor created successfully');
      onClose();
    } catch (err: any) {
      // 409 DUPLICATE_VENDOR — surface the existing vendor so the user can
      // close this modal and pick it from the dropdown rather than creating
      // a duplicate. The dedup check covers both sp_payees and the dashboard
      // payees table so vendors created from PO/Send-Payments forms collide.
      const errBody = err?.error;
      if (errBody?.code === 'DUPLICATE_VENDOR' && errBody.existing) {
        setDuplicate(errBody.existing);
      } else {
        toast.error('Failed to create vendor');
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 px-4 pt-[10vh] pb-8">
      <div className="relative w-full max-w-lg rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-lg font-semibold text-foreground">Add Vendor</h2>
          <button onClick={onClose} className="rounded-md p-1 hover:bg-muted">
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>
        <div className="px-6 py-5">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name Fields */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  value={formData.firstName}
                  onChange={(e) => handleAlphaInput('firstName', e.target.value)}
                  onBlur={() => markTouched('firstName')}
                  placeholder="John"
                  className={`mt-1 ${vendorFieldCls(touched.firstName ? fieldErrors : {}, 'firstName')}`}
                />
                {touched.firstName && <VendorFieldError message={fieldErrors.firstName} />}
              </div>
              <div>
                <label className="block text-sm font-medium">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  value={formData.lastName}
                  onChange={(e) => handleAlphaInput('lastName', e.target.value)}
                  onBlur={() => markTouched('lastName')}
                  placeholder="Smith"
                  className={`mt-1 ${vendorFieldCls(touched.lastName ? fieldErrors : {}, 'lastName')}`}
                />
                {touched.lastName && <VendorFieldError message={fieldErrors.lastName} />}
              </div>
            </div>

            {/* Contact Fields */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => update('email', e.target.value)}
                  onBlur={() => markTouched('email')}
                  placeholder="john@example.com"
                  className={`mt-1 ${vendorFieldCls(touched.email ? fieldErrors : {}, 'email')}`}
                />
                {touched.email && <VendorFieldError message={fieldErrors.email} />}
              </div>
              <div>
                <label className="block text-sm font-medium">Phone</label>
                <PhoneInput
                  value={formData.phone}
                  onValueChange={(digits) => update('phone', digits)}
                  onBlur={() => markTouched('phone')}
                  placeholder="123-456-7890"
                  className={`mt-1 ${vendorFieldCls(touched.phone ? fieldErrors : {}, 'phone')}`}
                  aria-invalid={!!fieldErrors.phone}
                />
                {touched.phone && <VendorFieldError message={fieldErrors.phone} />}
              </div>
              <div className="col-span-1 sm:col-span-2">
                <label className="block text-sm font-medium">
                  Company <span className="text-red-500">*</span>
                </label>
                <input
                  value={formData.company}
                  onChange={(e) => handleAlphaInput('company', e.target.value)}
                  onBlur={() => markTouched('company')}
                  placeholder="Company name"
                  className={`mt-1 ${vendorFieldCls(touched.company ? fieldErrors : {}, 'company')}`}
                />
                {touched.company && fieldErrors.company ? (
                  <VendorFieldError message={fieldErrors.company} />
                ) : (
                  <p className="mt-1 text-xs text-muted-foreground">If not a company, enter your First Name.</p>
                )}
              </div>
            </div>

            {duplicate && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
                <p className="text-sm font-medium text-amber-900">A vendor with this email already exists</p>
                <p className="mt-1 text-xs text-amber-800">
                  <span className="font-semibold">{duplicate.name}</span>
                  {duplicate.company ? ` (${duplicate.company})` : ''} · {duplicate.email}
                </p>
                <p className="mt-1 text-xs text-amber-800">
                  Close this dialog and pick the existing vendor from the dropdown.
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="h-9 rounded-lg border border-border px-4 text-sm font-medium text-foreground hover:bg-muted"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!isFormValid || createVendor.isPending}
                className="h-9 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {createVendor.isPending ? 'Creating...' : 'Create Vendor'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
